// ticket queue

import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { usePriorities } from '../../../hooks/usePriorities';
import { useStatuses } from '../../../hooks/useStatuses';
import { useCategories } from '../../../hooks/useCategories';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Ticket,
  Filter,
  RefreshCw,
  Settings,
  Search,
  ChevronDown,
  ChevronUp,
  CheckCircle,
  AlertTriangle,
  Clock,
  User,
  UserPlus,
  MessageSquare,
  ArrowUp,
  Download,
  Tag,
  Eye,
  MoreHorizontal,
  ChevronLeft,
  ChevronRight,
  Calendar,
  Clock3,
  AlertCircle,
  CheckCircle2,
  XCircle,
  Pause,
  Play,
  FileText,
  Paperclip,
  Star,
  Target,
  BarChart3,
  TrendingUp,
  Users,
  Activity,
  Bell,
  Plus,
  Edit,
  Trash2,
  Copy,
  Share2,
  Archive,
  ExternalLink,
  Lock,
  Unlock,
  Shield,
  Info,
  HelpCircle,
  X,
  Check,
  Minus,
  Pen
} from 'lucide-react';
import toast from 'react-hot-toast';
import { ticketService, isTicketApiSuccess } from '../../../api/ticketService';
import { useTicketRelationsEnabled } from '../../../contexts/AppConfigContext';
import { useFeaturePermissions } from '../../../components/globalfeatures';
import LinkedDuplicatesPanel from '../../../components/relations/LinkedDuplicatesPanel';
import AttachDuplicateModal from '../../../components/relations/AttachDuplicateModal';
import CloseAsDuplicateModal, {
  canShowCloseAsDuplicateAction,
  getTicketStatusCode,
} from '../../../components/relations/CloseAsDuplicateModal';
import { GitBranch, Link as LinkIcon } from 'lucide-react';
import { extractErrorMessage } from '../../../utils/errorHandler';
import { formatTicketFileUploadedAt } from '../../../utils/formatTicketFileUploadedAt';
import { formatRequesterDisplay } from '../../../utils/formatRequesterDisplay';
import { resolveAttachmentOpenUrl } from '../../../utils/resolveAttachmentOpenUrl';
import { teamService } from '../../../api/teamService';
import { userManagementService } from '../../../api/userManagementService';
import authService from '../../../api/authService';
import { consumeNotificationTicketSearch } from '../../../utils/notificationNavigation';
import { locationService } from '../../../api/locationService';
import { Modal } from '../../../components/ui/Modal';
import QueueConfidentialityLevelControl from '../../../components/tickets/QueueConfidentialityLevelControl';
import { Button } from '../../../components/ui/Button';
import MockDataService from '../../../services/MockDataService';
import { apiRequest } from '../../../services/apiClient';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

/** D7 — duplicate ticket cannot be linked when in these status_code values. */
const DUPLICATE_LINK_BLOCKED_STATUS_CODES = new Set([
  'resolved',
  'closed',
  'supervisor_approved',
]);

const normalizeTicketStatusCode = (ticket) => getTicketStatusCode(ticket);

/** Merge API ticket payload into view-modal state without stale statusCode from the list row. */
const mergeViewModalTicketDetail = (prev, detail) => {
  if (!detail || typeof detail !== 'object') return prev;

  const ticketId = detail.ticketId ?? detail.id;
  if (ticketId == null && detail.data && typeof detail.data === 'object') {
    return mergeViewModalTicketDetail(prev, detail.data);
  }

  let statusCode = getTicketStatusCode({
    status: detail.status,
    statusName: detail.statusName,
    statusCode: detail.statusCode,
  });
  if (detail.closedAt && statusCode !== 'closed' && statusCode !== 'resolved') {
    statusCode = 'closed';
  }
  if (!statusCode) {
    statusCode = getTicketStatusCode(prev);
  }

  const statusDisplay =
    (typeof detail.status === 'string' && detail.status.trim() ? detail.status : null)
    || detail.statusName
    || (statusCode === 'closed' ? 'Closed' : null)
    || (statusCode ? statusCode.replace(/_/g, ' ') : null)
    || prev?.status
    || 'open';

  return {
    ...prev,
    ...detail,
    id: ticketId || prev?.id,
    ticketNumber: detail.ticketNumber ?? prev?.ticketNumber,
    status: statusDisplay,
    statusCode,
    statusName: detail.statusName ?? statusDisplay,
    closedAt: detail.closedAt ?? prev?.closedAt,
    updatedAt: detail.updatedAt ?? prev?.updatedAt,
  };
};

const extractTicketDetailPayload = (wrapped) => {
  const rd = wrapped?.data;
  if (!rd || typeof rd !== 'object') return null;
  const payload = rd?.data ?? rd;
  if (!payload || typeof payload !== 'object') return null;
  const ticketId = payload.ticketId ?? payload.id;
  if (ticketId == null && payload.data && typeof payload.data === 'object') {
    return payload.data;
  }
  return ticketId != null ? payload : null;
};

function formatCustomFieldDisplayValue(value) {
  if (value === null || value === undefined) return '—';
  if (Array.isArray(value)) {
    const parts = value
      .map((item) => (item === null || item === undefined ? '' : String(item).trim()))
      .filter((item) => item.length > 0);
    return parts.length > 0 ? parts.join(', ') : '—';
  }
  if (typeof value === 'object') {
    try {
      return JSON.stringify(value);
    } catch {
      return String(value);
    }
  }
  const text = String(value).trim();
  return text.length > 0 ? text : '—';
}

/** Safe defaults when menu permissions are missing (e.g. during logout teardown). */
const EMPTY_SCREEN_PERMISSIONS = Object.freeze({
  canView: false,
  canUpdate: false,
  canInsert: false,
  canDelete: false,
});

function getScreenPermissionsFromSession() {
  try {
    const raw = sessionStorage.getItem('menu');
    if (!raw) {
      return EMPTY_SCREEN_PERMISSIONS;
    }
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') {
      return EMPTY_SCREEN_PERMISSIONS;
    }
    return parsed;
  } catch {
    return EMPTY_SCREEN_PERMISSIONS;
  }
}

function normalizeCustomFieldValues(raw) {
  if (raw == null) return {};
  if (typeof raw === 'string') {
    try {
      const parsed = JSON.parse(raw);
      return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {};
    } catch {
      return {};
    }
  }
  if (typeof raw === 'object' && !Array.isArray(raw)) {
    return { ...raw };
  }
  return {};
}

function findPriorityByToken(token, prioritiesList) {
  if (!token || !Array.isArray(prioritiesList)) return null;
  const normalized = String(token).toLowerCase();
  return (
    prioritiesList.find((p) => {
      const code = (p.priorityCode || '').toLowerCase();
      const name = (p.priorityName || '').toLowerCase();
      return code === normalized || name === normalized;
    }) || null
  );
}

function resolveUnassignedPriorityId(filters, prioritiesList) {
  if (filters?.priorityId != null && filters.priorityId !== '') {
    return filters.priorityId;
  }
  if (filters?.priority?.length > 0 && prioritiesList?.length > 0) {
    const match = findPriorityByToken(filters.priority[0], prioritiesList);
    return match?.priorityId ?? undefined;
  }
  return undefined;
}

function formatUnassignedCreatedDate(dateString) {
  if (!dateString) return '—';
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

function formatUnassignedCreatedTime(dateString) {
  if (!dateString) return '';
  return new Date(dateString).toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
  });
}

function getUnassignedStatusBadgeClass(status) {
  switch (String(status || '').toLowerCase()) {
    case 'open':
    case 'new':
      return 'text-blue-700 dark:text-blue-300 bg-blue-50 dark:bg-blue-900/40';
    case 'pending':
      return 'text-yellow-700 dark:text-yellow-300 bg-yellow-50 dark:bg-yellow-900/40';
    case 'resolved':
      return 'text-green-700 dark:text-green-300 bg-green-50 dark:bg-green-900/40';
    case 'closed':
      return 'text-gray-600 dark:text-gray-400 bg-gray-100 dark:bg-gray-700';
    case 'reopen':
      return 'text-purple-700 dark:text-purple-300 bg-purple-50 dark:bg-purple-900/40';
    default:
      return 'text-gray-600 dark:text-gray-400 bg-gray-100 dark:bg-gray-700';
  }
}

const TicketQueue = ({
  title = "Ticket Queue",
  description = "Manage and monitor all tickets in the queue",
  initialFilters = {},
  hideExtraActions = false
} = {}) => {
  const navigate = useNavigate();
  const [queueData, setQueueData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchLoading, setSearchLoading] = useState(false); // Separate loading state for search operations
  const [error, setError] = useState(null);
  // V1 parent-child badge map: { ticketsAsParent: { id: { childrenCount } },
  // ticketsAsChild: { id: { parentTicketId, parentTicketNumber, relationId } } }.
  // Only populated when the backend feature flag is on; otherwise stays empty so the
  // queue renders byte-identically to its pre-feature behavior.
  const ticketRelationsEnabled = useTicketRelationsEnabled();
  const duplicateTicketLinkPerms = useFeaturePermissions('duplicate_ticket_link');
  const canDuplicateLink = ticketRelationsEnabled && duplicateTicketLinkPerms.canView;
  const [relationBadges, setRelationBadges] = useState({ ticketsAsParent: {}, ticketsAsChild: {} });
  const [selectedTickets, setSelectedTickets] = useState([]);
  const { data: priorities = [], isSuccess: prioritiesReady } = usePriorities();
  /** Inquiry Priority Level Master — the only source for the assign dialog priority dropdown */
  const [inquiryPriorityLevels, setInquiryPriorityLevels] = useState([]);
  /** Operational priorities for first assignment — exclude Not Prioritised */
  const assignmentPriorities = useMemo(
    () =>
      (inquiryPriorityLevels || []).filter((p) => {
        const code = String(p.priorityLevelCode || p.code || '').toLowerCase().replace(/[\s-]+/g, '_');
        const name = String(p.priorityLevelName || p.name || '').toLowerCase();
        return code !== 'not_prioritised' && name !== 'not prioritised';
      }),
    [inquiryPriorityLevels]
  );
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const rows = await ticketService.getInquiryPriorityLevelsForCreate();
        if (!cancelled) setInquiryPriorityLevels(Array.isArray(rows) ? rows : []);
      } catch (e) {
        console.warn('Failed to load Inquiry Priority Levels for assign dialog', e);
      }
    })();
    return () => { cancelled = true; };
  }, []);
  const { data: statusesRaw = [] } = useStatuses();
  const statuses = useMemo(
    () => statusesRaw.filter((status) => status.isActive !== false),
    [statusesRaw]
  );
  const [filters, setFilters] = useState({
    priority: [],
    /** Server-side unassigned filter; single source of truth with backend priority_id */
    priorityId: null,
    status: [],
    category: [],
    slaStatus: [],
    assigned: initialFilters.assigned !== undefined ? initialFilters.assigned : null
  });
  const [sortBy, setSortBy] = useState('created-desc');
  const [searchTerm, setSearchTerm] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [showBulkActions, setShowBulkActions] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [totalElements, setTotalElements] = useState(0); // Store total elements from API for server-side pagination
  const [totalPages, setTotalPages] = useState(0); // Store total pages from API for server-side pagination
  const [assignModalOpen, setAssignModalOpen] = useState(false);
  const [currentTicketId, setCurrentTicketId] = useState(null);
  const [currentTicket, setCurrentTicket] = useState(null); // Store full ticket object for requesterId
  const [teams, setTeams] = useState([]);
  const [agents, setAgents] = useState([]);
  const [selectedTeam, setSelectedTeam] = useState('');
  const [selectedAgent, setSelectedAgent] = useState('');
  /** 'me' | 'team' | 'agent' — single flow: pick type, then one "Assign ticket" action */
  const [assignMode, setAssignMode] = useState('me');
  const [assignSubmitting, setAssignSubmitting] = useState(false);
  const [selectedPriorityId, setSelectedPriorityId] = useState('');
  /** Shown when assign API returns 409 or UI detects ticket already has another assignee */
  const [assignConflictOpen, setAssignConflictOpen] = useState(false);
  const [assignConflictMessage, setAssignConflictMessage] = useState('');
  const [loadingTeams, setLoadingTeams] = useState(false);
  const [loadingAgents, setLoadingAgents] = useState(false);
  const [assignResponsibleRoleName, setAssignResponsibleRoleName] = useState('');
  const [viewTicketModalOpen, setViewTicketModalOpen] = useState(false);
  /** When true (opened via View), ticket modal is read-only — no status/category/location edits. */
  const [viewTicketModalViewOnly, setViewTicketModalViewOnly] = useState(false);
  const [selectedTicketForView, setSelectedTicketForView] = useState(null);
  /** Inquiry classification edit (email tickets with NULL inquiry masters). */
  const [savingInquiryClassification, setSavingInquiryClassification] = useState(false);
  const [inquiryTypes, setInquiryTypes] = useState([]);
  const [inquiryCategories, setInquiryCategories] = useState([]);
  const [inquirySubCategories, setInquirySubCategories] = useState([]);
  const [draftInquiryTypeId, setDraftInquiryTypeId] = useState('');
  const [draftInquiryCategoryId, setDraftInquiryCategoryId] = useState('');
  const [draftInquirySubCategoryId, setDraftInquirySubCategoryId] = useState('');
  const [draftInquiryPriorityLevelId, setDraftInquiryPriorityLevelId] = useState('');
  const { data: categories = [] } = useCategories();
  const filterableCategories = useMemo(
    () =>
      categories.filter(
        (cat) =>
          cat.isActive !== false &&
          cat.categoryId != null &&
          (cat.categoryName || cat.name || '') !== 'Select a category...'
      ),
    [categories]
  );
  const [exporting, setExporting] = useState(false);
  const [exportMenuOpen, setExportMenuOpen] = useState(false);
  // Location states
  const [countries, setCountries] = useState([]);
  const [counties, setCounties] = useState([]);
  const [subCounties, setSubCounties] = useState([]);
  const [wards, setWards] = useState([]);
  const [selectedCountryId, setSelectedCountryId] = useState('');
  const [selectedCountyId, setSelectedCountyId] = useState('');
  const [selectedSubCountyId, setSelectedSubCountyId] = useState('');
  const [selectedWardId, setSelectedWardId] = useState('');
  const [updatingLocation, setUpdatingLocation] = useState(false);
  const [loadingLocations, setLoadingLocations] = useState({
    countries: false,
    counties: false,
    subCounties: false,
    wards: false
  });
  // User details states
  const [requesterUser, setRequesterUser] = useState(null);
  const [assignedUser, setAssignedUser] = useState(null);
  const [loadingRequester, setLoadingRequester] = useState(false);
  const [loadingAssigned, setLoadingAssigned] = useState(false);
  const [viewModalAttachments, setViewModalAttachments] = useState([]);
  const [loadingViewModalAttachments, setLoadingViewModalAttachments] = useState(false);
  const [viewModalRelationsBundle, setViewModalRelationsBundle] = useState(null);
  const [viewModalRelationsLoading, setViewModalRelationsLoading] = useState(false);
  const [showAttachDuplicateModal, setShowAttachDuplicateModal] = useState(false);
  /** Unassigned list row selected for Duplicate Ticket toolbar action. */
  const [selectedDuplicateTicketId, setSelectedDuplicateTicketId] = useState(null);
  /** When true, row radio buttons are shown for duplicate linking. */
  const [duplicateSelectMode, setDuplicateSelectMode] = useState(false);
  const [showQueueDuplicateModal, setShowQueueDuplicateModal] = useState(false);
  const [showCloseAsDuplicateModal, setShowCloseAsDuplicateModal] = useState(false);
  const [closeAsDuplicateReason, setCloseAsDuplicateReason] = useState('');
  const [closeAsDuplicateError, setCloseAsDuplicateError] = useState('');
  const [closingAsDuplicate, setClosingAsDuplicate] = useState(false);
  // Cache user data to avoid re-fetching on every search
  const [cachedUserData, setCachedUserData] = useState(null);
  const [initialStats, setInitialStats] = useState(null);
  const fetchQueueDataRef = useRef(null);
  /** After true, page/filter/search effects may fetch; mount owns the first load. */
  const unassignedBootstrappedRef = useRef(false);
  const hadSearchTermRef = useRef(false);
  /** Applied to the next fetchQueueData before React commits setSearchTerm (notification bootstrap). */
  const notificationSearchOverrideRef = useRef(null);
  const masterDataInitRef = useRef(false);
  /** Agents may only self-assign; team-lead, admin, super-admin, etc. may assign to team or other agents. */
  const assignScopeSelfOnly = authService.getCurrentUser()?.role === 'agent';
  const location = useLocation();

  /** TEAM_LEAD / ADMIN / SUPER_ADMIN (Supervisor maps to ADMIN in RBAC) may classify email tickets. */
  const canClassifyInquiry = useMemo(() => {
    const user = authService.getCurrentUser() || {};
    let details = {};
    try {
      details = JSON.parse(sessionStorage.getItem('userDetails') || '{}');
    } catch {
      details = {};
    }
    const raw = user.roleCode || user.role || details.roleCode || details.role || details.userRole || '';
    const code = String(raw).toUpperCase().replace(/[\s-]+/g, '_');
    return code === 'TEAM_LEAD' || code === 'ADMIN' || code === 'SUPER_ADMIN' || code === 'SUPERVISOR';
  }, []);

  const hasPermission = getScreenPermissionsFromSession();

  useEffect(() => {
    if (!prioritiesReady || masterDataInitRef.current) {
      return undefined;
    }
    masterDataInitRef.current = true;

    const initializeData = async () => {
      if (title === 'Unassigned Tickets') {
        const notificationSearch = consumeNotificationTicketSearch();
        if (notificationSearch) {
          hadSearchTermRef.current = true;
          setSearchTerm(notificationSearch);
          notificationSearchOverrideRef.current = notificationSearch;
        }
        try {
          await fetchQueueData(0, 50, false, false, priorities);
        } finally {
          notificationSearchOverrideRef.current = null;
        }
        unassignedBootstrappedRef.current = true;
        return;
      }
      const notificationSearch = consumeNotificationTicketSearch();
      if (notificationSearch) {
        setSearchTerm(notificationSearch);
      }
      await fetchQueueData(0, 50, false, false, priorities);
    };
    unassignedBootstrappedRef.current = false;
    hadSearchTermRef.current = false;
    initializeData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prioritiesReady, priorities]);

  // For unassigned tickets, refetch when page changes (server-side pagination)
  useEffect(() => {
    if (title !== 'Unassigned Tickets' || !unassignedBootstrappedRef.current) {
      return undefined;
    }
    fetchQueueData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage]);

  // For unassigned tickets, refetch when filters change (server-side filtering)
  useEffect(() => {
    if (title !== 'Unassigned Tickets' || !unassignedBootstrappedRef.current) {
      return undefined;
    }
    if (currentPage !== 1) {
      setCurrentPage(1);
      return undefined;
    }
    const timeoutId = setTimeout(() => {
      fetchQueueData();
    }, 100);
    return () => clearTimeout(timeoutId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.priorityId, filters.status, filters.category]);

  // Reset to page 1 when filters or search change for unassigned tickets
  useEffect(() => {
    if (
      title === 'Unassigned Tickets' &&
      unassignedBootstrappedRef.current &&
      currentPage !== 1
    ) {
      setCurrentPage(1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, searchTerm]);

  // Debounced search for unassigned tickets (server-side search)
  useEffect(() => {
    if (title !== 'Unassigned Tickets' || !unassignedBootstrappedRef.current) {
      return undefined;
    }
    const trimmed = searchTerm?.trim() || '';
    if (trimmed) {
      hadSearchTermRef.current = true;
    } else if (!hadSearchTermRef.current) {
      return undefined;
    }
    if (currentPage !== 1) {
      setCurrentPage(1);
      return undefined;
    }
    const timeoutId = setTimeout(() => {
      fetchQueueData(0, 50, true, true);
    }, trimmed ? 500 : 300);
    return () => clearTimeout(timeoutId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm]);

  // Notification click while already on Unassigned Tickets (same route, new history entry)
  useEffect(() => {
    if (title !== 'Unassigned Tickets' || !unassignedBootstrappedRef.current) {
      return undefined;
    }
    const notificationSearch = consumeNotificationTicketSearch();
    if (!notificationSearch) {
      return undefined;
    }
    hadSearchTermRef.current = true;
    setSearchTerm(notificationSearch);
    return undefined;
  }, [title, location.key]);

  // Batched parent-child badge fetch — runs whenever the visible page of tickets
  // changes (and only when the V1 feature flag is on). One round trip per page; the
  // backend caps the batch at 500 ids and returns absent ids implicitly so callers
  // don't need a special case for "no relations".
  useEffect(() => {
    if (!ticketRelationsEnabled) return;
    const tickets = queueData?.ticketQueue || [];
    if (tickets.length === 0) {
      setRelationBadges({ ticketsAsParent: {}, ticketsAsChild: {} });
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const ids = tickets
          .map((t) => Number(t.id ?? t.ticketId))
          .filter((n) => Number.isFinite(n));
        const res = await ticketService.getRelationBadges(ids);
        const apiData = res?.data;
        if (cancelled) return;
        if (apiData?.success === false) {
          console.debug('[TicketQueue] badge fetch responded success=false:', apiData?.message);
          return;
        }
        const data = apiData?.data || apiData || {};
        setRelationBadges({
          ticketsAsParent: data.ticketsAsParent || {},
          ticketsAsChild: data.ticketsAsChild || {},
        });
      } catch (err) {
        const status = err?.response?.status || err?.status;
        if (status === 404) {
          // Backend has the flag off; suppress noise.
          console.debug('[TicketQueue] badges endpoint returned 404; feature off');
        } else {
          console.warn('[TicketQueue] badge fetch failed:', err?.message || err);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [ticketRelationsEnabled, queueData?.ticketQueue]);

  // Load teams and agents when modal opens (not needed for agents — self-assign only)
  useEffect(() => {
    if (!assignModalOpen) {
      return;
    }
    if (assignScopeSelfOnly) {
      setAssignMode('me');
      setSelectedTeam('');
      setSelectedAgent('');
      setTeams([]);
      setAgents([]);
      return;
    }
    setAssignMode('agent');
    loadTeamsAndAgents();
  }, [assignModalOpen, assignScopeSelfOnly]);

  const buildUnassignedQueryParams = useCallback(
    (overrides = {}) => {
      const priorityList = overrides.prioritiesList ?? priorities;
      const overrideSearch = notificationSearchOverrideRef.current?.trim();
      const searchParam =
        overrides.search !== undefined
          ? overrides.search
          : overrideSearch
            ? overrideSearch
            : searchTerm?.trim()
              ? searchTerm.trim()
              : undefined;
      const statusParam =
        filters.status?.length > 0 ? filters.status[0] : undefined;
      let filterCategoryIdParam;
      if (filters.category?.length > 0 && categories.length > 0) {
        const selectedCategory = categories.find((cat) =>
          filters.category.includes(cat.categoryName || '')
        );
        if (selectedCategory?.categoryId) {
          filterCategoryIdParam = selectedCategory.categoryId;
        }
      }
      const priorityId =
        overrides.priorityId !== undefined
          ? overrides.priorityId
          : resolveUnassignedPriorityId(filters, priorityList);
      const params = {
        page: overrides.page ?? 0,
        size: overrides.size ?? 10,
        search: searchParam,
        status: statusParam,
        filterCategoryId: filterCategoryIdParam
      };
      if (priorityId != null && priorityId !== '') {
        params.priorityId = priorityId;
      }
      return params;
    },
    [searchTerm, filters, categories, priorities]
  );

  const fetchQueueData = async (
    page = 0,
    size = 50,
    skipStatsUpdate = false,
    isSearchOperation = false,
    prioritiesOverride = null
  ) => {
    try {
      // Use searchLoading for search operations to avoid full page reload
      if (isSearchOperation) {
        setSearchLoading(true);
      } else {
        setLoading(true);
      }
      const availablePriorities = prioritiesOverride ?? priorities;

      // Cache user data to avoid re-fetching on every search (logged-in agent — not the ticket requester)
      let userdata = cachedUserData;
      if (!userdata) {
        const agentUserId = sessionStorage.getItem('userId') || localStorage.getItem('userId');
        if (agentUserId) {
          userdata = await apiGet(`/gateway/registration/api/signup/organization/users/${agentUserId}`);
          setCachedUserData(userdata);
        } else {
          userdata = { data: {} };
          console.warn('TicketQueue: userId missing from session/local storage; skipping agent profile fetch');
        }
      }
      //console.log("userdata ==============> ", userdata);
      const categoryId = userdata?.data?.categoryId;
      const categoryName = userdata?.data?.categoryName || userdata?.data?.category?.categoryName || '';

      // Use unassigned endpoint if this is the Unassigned Tickets page
      let response = null;

      // Special handling for unassigned tickets page:
      // Tickets with category "general" or ID 1 should be visible to ALL agents
      if (title === "Unassigned Tickets") {
        const pageSize = 10;
        const apiPage = currentPage - 1;
        const unassignedResponse = await ticketService.getUnassignedTickets(
          buildUnassignedQueryParams({
            page: apiPage,
            size: pageSize,
            prioritiesList: availablePriorities
          })
        );

        const tickets = unassignedResponse?.data?.data?.content || [];
        const totalElements = unassignedResponse?.data?.data?.totalElements || 0;
        const totalPages = unassignedResponse?.data?.data?.totalPages || 0;

        // Store pagination info for server-side pagination
        setTotalElements(totalElements);
        setTotalPages(totalPages);

        // Return tickets directly (backend already handles filtering logic)
        response = {
          data: {
            success: true,
            message: 'Unassigned tickets retrieved successfully',
            data: {
              content: tickets, // Backend already returns filtered tickets
              totalElements: totalElements,
              totalPages: totalPages,
              pageNumber: apiPage,
              pageSize: pageSize,
              first: apiPage === 0,
              last: apiPage >= totalPages - 1
            }
          }
        };
      } else {
        // For other pages (not unassigned), use existing logic
        if (categoryId && categoryId !== null && categoryId !== undefined && categoryId !== '') {
          response = await ticketService.getAllTickets({ page, size, categoryId });
        } else {
          response = await ticketService.getAllTickets({ page, size });
        }
      }
      // Backend returns ResponseBean: { status, message, data, success, timestamp }
      // data is PaginatedResponse with content, pageNumber, pageSize, totalElements, etc.
      const responseData = response?.data;

      if (responseData?.success && responseData?.data) {
        const paginatedData = responseData.data;
        const tickets = paginatedData.content || [];

        // Transform backend tickets to frontend format
        const transformedTickets = tickets.map(ticket => {
          // Ensure assigneeId is properly handled (null, undefined, or empty string means unassigned)
          const assigneeId = ticket.assigneeId || null;
          const isAssigned = assigneeId !== null && assigneeId !== undefined && assigneeId !== '';

          // Prefer Inquiry Priority Level; fall back to legacy ticket_priorities only for old tickets
          let priority = 'medium';
          let priorityName = 'Medium';
          let priorityId = ticket.priorityId;

          if (ticket.inquiryPriorityLevelName) {
            priority = String(ticket.inquiryPriorityLevelName).toLowerCase();
            priorityName = ticket.inquiryPriorityLevelName;
          } else if (priorityId && availablePriorities.length > 0) {
            const priorityObj = availablePriorities.find(p => p.priorityId === priorityId || p.priorityId?.toString() === priorityId?.toString());
            if (priorityObj) {
              priority = (priorityObj.priorityCode || priorityObj.priorityName || 'medium').toLowerCase();
              priorityName = priorityObj.priorityName || priorityObj.priorityCode || 'Medium';
            }
          } else if (ticket.priority) {
            priority = ticket.priority.toLowerCase();
            priorityName = ticket.priority;
          }

          // Determine SLA status from backend data
          let slaStatus = 'on-track';
          if (ticket.slaApplicable === false) {
            slaStatus = 'on-track';
          } else if (ticket.slaIsEscalated) {
            slaStatus = 'breach';
          } else if (ticket.slaIsBreached) {
            slaStatus = 'breach';
          } else if (ticket.slaRemainingMinutes !== null && ticket.slaRemainingMinutes !== undefined) {
            if (ticket.slaRemainingMinutes < 60) {
              slaStatus = 'at-risk';
            } else {
              slaStatus = 'on-track';
            }
          }

          return {
            id: ticket.ticketId || ticket.ticketNumber,
            ticketNumber: ticket.ticketNumber,
            title: ticket.subject,
            subject: ticket.subject,
            description: ticket.description || '',
            status: ticket.status?.toLowerCase() || 'open',
            statusId: ticket.statusId, // Store statusId if available from backend
            priority: priority,
            priorityName: priorityName,
            priorityId: priorityId,
            inquiryTypeId: ticket.inquiryTypeId,
            inquiryTypeName: ticket.inquiryTypeName,
            inquiryCategoryId: ticket.inquiryCategoryId,
            inquiryCategoryName: ticket.inquiryCategoryName,
            inquirySubCategoryId: ticket.inquirySubCategoryId,
            inquirySubCategoryName: ticket.inquirySubCategoryName,
            inquiryPriorityLevelId: ticket.inquiryPriorityLevelId,
            inquiryPriorityLevelName: ticket.inquiryPriorityLevelName,
            confidentialityLevel: ticket.confidentialityLevel,
            category: ticket.inquiryCategoryName || ticket.category || 'Uncategorized',
            categoryId: ticket.categoryId, // Store categoryId for updates
            createdAt: ticket.createdAt,
            updatedAt: ticket.updatedAt || ticket.createdAt,
            dueDate: ticket.resolvedAt || ticket.updatedAt,
            assignedAgent: isAssigned ? `Agent ${assigneeId}` : null,
            assigneeId: assigneeId, // Keep original assigneeId for reference
            requesterId: ticket.requesterId,
            requesterFullName: ticket.requesterFullName,
            requesterEmail: ticket.requesterEmail,
            assignedTime: ticket.assignedTime,
            slaStatus: slaStatus,
            slaTimeRemaining: ticket.slaRemainingMinutes, // Use backend calculated value
            slaElapsedMinutes: ticket.slaElapsedMinutes,
            slaIsBreached: ticket.slaIsBreached,
            slaIsEscalated: ticket.slaIsEscalated,
            attachments: ticket.attachmentCount ? Array(ticket.attachmentCount).fill(null) : [],
            // Location fields
            countryId: ticket.countryId,
            countyId: ticket.countyId,
            subCountyId: ticket.subCountyId,
            wardId: ticket.wardId,
            countryName: ticket.countryName,
            countyName: ticket.countyName,
            subCountyName: ticket.subCountyName,
            wardName: ticket.wardName
          };
        });


        let stats = initialStats;
        if (title !== 'Unassigned Tickets' && !skipStatsUpdate) {
          stats = {
            total: paginatedData.totalElements || 0,
            new: transformedTickets.filter(t => t.status === 'open' || t.status === 'new').length,
            highPriority: transformedTickets.filter(t => t.priority === 'high' || t.priority === 'critical').length,
            overdue: transformedTickets.filter(t => t.slaStatus === 'breach').length,
            unassigned: transformedTickets.filter(t => !t.assignedAgent).length,
            slaCompliance: transformedTickets.length > 0
              ? Math.round((transformedTickets.filter(t => t.slaStatus === 'on-track').length / transformedTickets.length) * 100)
              : 0,
            critical: transformedTickets.filter(t => (t.priority || '').toLowerCase() === 'critical').length,
            high: transformedTickets.filter(t => (t.priority || '').toLowerCase() === 'high').length,
            medium: transformedTickets.filter(t => (t.priority || '').toLowerCase() === 'medium').length,
            low: transformedTickets.filter(t => (t.priority || '').toLowerCase() === 'low').length,
            urgent: transformedTickets.filter(t => (t.priority || '').toLowerCase() === 'urgent').length
          };
          if (!initialStats) {
            setInitialStats(stats);
          }
        }
        if (!stats && initialStats) {
          stats = initialStats;
        }

        // Create filters from tickets
        const priorities = [...new Set(transformedTickets.map(t => t.priority))];
        const statuses = [...new Set(transformedTickets.map(t => t.status))];
        const categories = [...new Set(transformedTickets.map(t => t.category))];
        const slaStatuses = [...new Set(transformedTickets.map(t => t.slaStatus))];

        // Update queueData - for search operations, this smoothly replaces the table data
        // without clearing it first, so the table remains visible during the update
        setQueueData(prevData => ({
          ticketQueue: transformedTickets, // Directly set new tickets (smooth patch)
          queueStats:
            title === 'Unassigned Tickets'
              ? prevData?.queueStats || {}
              : stats || prevData?.queueStats || {},
          queueFilters: {
            priorities,
            statuses,
            categories,
            slaStatuses
          },
          bulkActions: prevData?.bulkActions || [
            { id: 'assign', name: 'Assign' },
            { id: 'escalate', name: 'Escalate' },
            { id: 'resolve', name: 'Resolve' },
            { id: 'close', name: 'Close' },
            { id: 'archive', name: 'Archive' }
          ],
          quickActions: prevData?.quickActions || [
            { id: 'view', name: 'View', icon: 'eye', description: 'View ticket details' },
            { id: 'assign', name: 'Assign', icon: 'user-check', description: 'Assign ticket' },
            { id: 'escalate', name: 'Escalate', icon: 'arrow-up', description: 'Escalate ticket' },
            { id: 'resolve', name: 'Resolve', icon: 'check', description: 'Resolve ticket' }
          ],
          sortOptions: prevData?.sortOptions || [
            { id: 'created-desc', name: 'Created (Newest)', field: 'createdAt', direction: 'desc' },
            { id: 'created-asc', name: 'Created (Oldest)', field: 'createdAt', direction: 'asc' },
            { id: 'priority-desc', name: 'Priority (High to Low)', field: 'priority', direction: 'desc' },
            { id: 'priority-asc', name: 'Priority (Low to High)', field: 'priority', direction: 'asc' },
            { id: 'updated-desc', name: 'Updated (Recent)', field: 'updatedAt', direction: 'desc' }
          ]
        }));
      } else {
        setError(responseData?.message || 'Failed to load ticket queue data');
        toast.error(responseData?.message || 'Failed to load ticket queue');
      }
    } catch (err) {
      setError('Error loading ticket queue data');
      console.error('Queue data error:', err);
      toast.error(err?.response?.data?.message || 'Error loading ticket queue');
    } finally {
      // Reset appropriate loading state
      if (isSearchOperation) {
        setSearchLoading(false);
      } else {
        setLoading(false);
      }
    }
  };

  fetchQueueDataRef.current = fetchQueueData;

  useEffect(() => {
    if (title !== 'Unassigned Tickets') {
      return undefined;
    }
    const onRealtimeRefresh = () => {
      fetchQueueDataRef.current?.(undefined, undefined, true, true);
    };
    window.addEventListener('hd:queue-realtime-refresh', onRealtimeRefresh);
    return () => window.removeEventListener('hd:queue-realtime-refresh', onRealtimeRefresh);
  }, [title]);

  const handleRefresh = async () => {
    toast.success('Refreshing ticket queue...');
    await fetchQueueData();
    toast.success('Queue refreshed successfully');
  };

  const formatDateTimeExport = (isoString) => {
    if (!isoString) return '-';
    try {
      const d = new Date(isoString);
      if (isNaN(d.getTime())) return '-';
      return d.toLocaleString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit' });
    } catch {
      return '-';
    }
  };

  const escapeCsvCell = (val) => {
    if (val == null || val === '') return '';
    const s = String(val);
    if (/[,"\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
    return s;
  };

  const fetchUnassignedForExport = async () => {
    const buildParams = (page, size) =>
      buildUnassignedQueryParams({ page, size });
    const first = await ticketService.getUnassignedTickets(buildParams(0, 10));
    const total = first?.data?.data?.totalElements ?? 0;
    if (total <= 0) return [];
    const full = await ticketService.getUnassignedTickets(buildParams(0, total));
    const content = full?.data?.data?.content || [];
    return content.map((ticket) => ({
      ticketNumber: ticket.ticketNumber || ticket.ticketId || 'N/A',
      subject: ticket.subject || '',
      category: ticket.inquiryCategoryName || ticket.category || 'Uncategorized',
      status: (ticket.status || 'open').toLowerCase(),
      assigned: ticket.assigneeId ? 'Assigned' : 'Unassigned',
      createdAt: ticket.createdAt
    }));
  };

  const handleExportCsv = async () => {
    setExportMenuOpen(false);
    setExporting(true);
    try {
      const rows = await fetchUnassignedForExport();
      if (rows.length === 0) {
        toast.error('No tickets to export');
        return;
      }
      const headers = ['S.NO', 'Ticket Number', 'Subject', 'Category', 'Status', 'Assigned', 'Created'];
      const csvRows = rows.map((r, i) => [
        i + 1,
        r.ticketNumber,
        r.subject,
        r.category,
        r.status,
        r.assigned,
        formatDateTimeExport(r.createdAt)
      ].map(escapeCsvCell));
      const csv = [headers.map(escapeCsvCell).join(','), ...csvRows.map((r) => r.join(','))].join('\r\n');
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `unassigned-tickets-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Tickets exported as CSV');
    } catch (e) {
      toast.error(extractErrorMessage(e, 'Export failed'));
    } finally {
      setExporting(false);
    }
  };

  const handleExportPdf = async () => {
    setExportMenuOpen(false);
    setExporting(true);
    try {
      const rows = await fetchUnassignedForExport();
      if (rows.length === 0) {
        toast.error('No tickets to export');
        return;
      }
      const doc = new jsPDF({ orientation: 'landscape' });
      doc.setFontSize(14);
      doc.text('Unassigned Tickets', 14, 15);
      doc.setFontSize(10);
      const body = rows.map((r, i) => [
        i + 1,
        r.ticketNumber,
        (r.subject || '').substring(0, 35),
        (r.category || '').substring(0, 15),
        r.status,
        r.assigned,
        formatDateTimeExport(r.createdAt)
      ]);
      autoTable(doc, {
        startY: 22,
        head: [['S.NO', 'Ticket Number', 'Subject', 'Category', 'Status', 'Assigned', 'Created']],
        body,
        styles: { fontSize: 7 }
      });
      doc.save(`unassigned-tickets-${new Date().toISOString().slice(0, 10)}.pdf`);
      toast.success('Tickets exported as PDF');
    } catch (e) {
      toast.error(extractErrorMessage(e, 'Export failed'));
    } finally {
      setExporting(false);
    }
  };

  const handleSettings = () => {
    navigate('/agent/settings');
  };

  const handleTicketSelect = (ticketId) => {
    setSelectedTickets(prev =>
      prev.includes(ticketId)
        ? prev.filter(id => id !== ticketId)
        : [...prev, ticketId]
    );
  };

  const handleSelectAll = () => {
    if (selectedTickets.length === filteredTickets.length) {
      setSelectedTickets([]);
    } else {
      setSelectedTickets(filteredTickets.map(ticket => ticket.id));
    }
  };

  const handleFilterChange = (filterType, value) => {
    setFilters(prev => ({
      ...prev,
      [filterType]: prev[filterType].includes(value)
        ? prev[filterType].filter(item => item !== value)
        : [...prev[filterType], value]
    }));
  };
  const visiblePages = 5;

  const getPageNumbers = () => {
    // Use displayTotalPages for unassigned tickets, or calculate from filteredTickets for others
    const maxPages = title === "Unassigned Tickets"
      ? (totalPages || 1)
      : Math.ceil(filteredTickets.length / itemsPerPage);

    let start = Math.max(2, currentPage - Math.floor(visiblePages / 2));
    let end = start + visiblePages - 1;

    if (end >= maxPages) {
      end = maxPages - 1;
      start = Math.max(2, end - visiblePages + 1);
    }

    const pages = [];
    for (let i = start; i <= end; i++) {
      pages.push(i);
    }

    return pages;
  };

  const getFilteredTickets = () => {
    if (!queueData?.ticketQueue) return [];

    if (title === 'Unassigned Tickets') {
      return queueData.ticketQueue;
    }

    let filtered = queueData.ticketQueue;

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(ticket =>
        ticket.subject?.toLowerCase().includes(term) ||
        ticket.ticketNumber?.toLowerCase().includes(term) ||
        ticket.description?.toLowerCase().includes(term)
      );
    }

    // Filter by status
    if (filters.status.length > 0) {
      filtered = filtered.filter(ticket =>
        filters.status.includes(ticket.status)
      );
    }

    // Filter by priority
    if (filters.priority.length > 0) {
      filtered = filtered.filter(ticket =>
        filters.priority.includes(ticket.priority)
      );
    }

    // Filter by category
    if (filters.category.length > 0) {
      filtered = filtered.filter(ticket =>
        filters.category.includes(ticket.category)
      );
    }

    // Filter by SLA status
    if (filters.slaStatus.length > 0) {
      filtered = filtered.filter(ticket =>
        filters.slaStatus.includes(ticket.slaStatus)
      );
    }

    // Filter by assigned status
    if (filters.assigned !== null) {
      if (filters.assigned === true) {
        filtered = filtered.filter(ticket => ticket.assignedAgent);
      } else {
        filtered = filtered.filter(ticket => !ticket.assignedAgent);
      }
    }

    return filtered;
  };

  const calculateStats = (tickets) => {
    return {
      total: tickets.length,
      byStatus: {
        open: tickets.filter(t => t.status === 'open' || t.status === 'new').length,
        inProgress: tickets.filter(t => t.status === 'pending' || t.status === 'in_progress').length,
        resolved: tickets.filter(t => t.status === 'resolved').length,
        closed: tickets.filter(t => t.status === 'closed').length
      },
      byPriority: {
        low: tickets.filter(t => t.priority === 'low').length,
        medium: tickets.filter(t => t.priority === 'medium').length,
        high: tickets.filter(t => t.priority === 'high').length,
        critical: tickets.filter(t => t.priority === 'critical').length
      },
      slaBreaches: tickets.filter(t => t.slaStatus === 'breach').length,
      unassigned: tickets.filter(t => !t.assignedAgent).length
    };
  };

  const handleBulkAssign = async (assigneeId, priorityId) => {
    if (selectedTickets.length === 0) {
      toast.error('Please select tickets to assign');
      return;
    }
    if (!priorityId) {
      toast.error('Please select a priority');
      return;
    }

    try {
      // Get tickets data to extract requesterId for each ticket
      const tickets = queueData?.tickets || [];
      const promises = selectedTickets.map(ticketId => {
        // Find the ticket to get its requesterId
        const ticket = tickets.find(t => t.id === ticketId);
        const requesterId = ticket?.requesterId || null;
        return ticketService.assignTicket(ticketId, assigneeId, requesterId, priorityId);
      });

      const results = await Promise.allSettled(promises);
      const successCount = results.filter(r => r.status === 'fulfilled').length;

      toast.success(`${successCount} ticket(s) assigned successfully`);
      setSelectedTickets([]);
      await fetchQueueData();
    } catch (error) {
      console.error('Bulk assign error:', error);
      toast.error('Some assignments failed');
    }
  };

  const handleBulkStatusUpdate = async (statusId) => {
    if (selectedTickets.length === 0) {
      toast.error('Please select tickets to update');
      return;
    }

    try {
      const promises = selectedTickets.map(ticketId =>
        ticketService.updateTicketStatus(ticketId, statusId)
      );

      const results = await Promise.allSettled(promises);
      const successCount = results.filter(r => r.status === 'fulfilled').length;

      toast.success(`${successCount} ticket(s) updated successfully`);
      setSelectedTickets([]);
      await fetchQueueData();
    } catch (error) {
      console.error('Bulk status update error:', error);
      toast.error('Some updates failed');
    }
  };

  const handleBulkAction = (actionId) => {
    if (selectedTickets.length === 0) {
      toast.error('Please select tickets to perform bulk action');
      return;
    }

    // For escalation, filter out closed and resolved tickets
    if (actionId === 'escalate') {
      const validTickets = selectedTickets.filter(ticketId => {
        const ticket = filteredTickets.find(t => t.id === ticketId);
        if (!ticket) return false;
        const status = ticket.status?.toLowerCase();
        return status !== 'closed' && status !== 'resolved';
      });

      if (validTickets.length === 0) {
        toast.error('Cannot escalate. Selected tickets are closed or resolved.');
        return;
      }

      if (validTickets.length < selectedTickets.length) {
        toast.warning(`${selectedTickets.length - validTickets.length} ticket(s) skipped (closed/resolved). Escalating ${validTickets.length} ticket(s).`);
      }

      navigate('/agent/tickets/escalation', { state: { ticketIds: validTickets } });
      return;
    }

    const actionMap = {
      'assign': () => navigate('/agent/tickets/assignment', { state: { ticketIds: selectedTickets } }),
      'resolve': () => navigate('/agent/tickets/resolution', { state: { ticketIds: selectedTickets } }),
      'close': () => {
        toast.success(`Closing ${selectedTickets.length} ticket(s)`);
        setSelectedTickets([]);
        setShowBulkActions(false);
      },
      'archive': () => {
        toast.success(`Archiving ${selectedTickets.length} ticket(s)`);
        setSelectedTickets([]);
        setShowBulkActions(false);
      }
    };

    if (actionMap[actionId]) {
      actionMap[actionId]();
    } else {
      toast.success(`Bulk action "${actionId}" performed on ${selectedTickets.length} ticket(s)`);
      setSelectedTickets([]);
      setShowBulkActions(false);
    }
  };

  const handleViewTicket = async (ticketId, { viewOnly = false } = {}) => {
    // For unassigned tickets page, show popup modal instead of navigating
    if (title === "Unassigned Tickets") {
      const ticket = filteredTickets.find(t => t.id === ticketId);
      if (ticket) {
        let ticketForModal = {
          ...ticket,
          customFieldValues: normalizeCustomFieldValues(ticket.customFieldValues),
        };
        try {
          const detailRes = await ticketService.getTicketById(ticketId);
          const rd = detailRes?.data;
          const detail = rd?.data ?? rd;
          if (detail && typeof detail === 'object') {
            ticketForModal = {
              ...ticket,
              ...detail,
              id: detail.ticketId || detail.id || ticket.id,
              // Prefer Inquiry masters from TicketResponse over legacy category/priority
              inquiryTypeId: detail.inquiryTypeId ?? ticket.inquiryTypeId,
              inquiryTypeName: detail.inquiryTypeName ?? ticket.inquiryTypeName,
              inquiryCategoryId: detail.inquiryCategoryId ?? ticket.inquiryCategoryId,
              inquiryCategoryName: detail.inquiryCategoryName ?? ticket.inquiryCategoryName,
              inquirySubCategoryId: detail.inquirySubCategoryId ?? ticket.inquirySubCategoryId,
              inquirySubCategoryName: detail.inquirySubCategoryName ?? ticket.inquirySubCategoryName,
              inquiryPriorityLevelId: detail.inquiryPriorityLevelId ?? ticket.inquiryPriorityLevelId,
              inquiryPriorityLevelName: detail.inquiryPriorityLevelName ?? ticket.inquiryPriorityLevelName,
              confidentialityLevel: detail.confidentialityLevel ?? ticket.confidentialityLevel,
              priorityName:
                detail.inquiryPriorityLevelName
                || ticket.inquiryPriorityLevelName
                || detail.priorityName
                || ticket.priorityName,
              priority:
                (detail.inquiryPriorityLevelName
                  || ticket.inquiryPriorityLevelName
                  || detail.priorityName
                  || ticket.priorityName
                  || ticket.priority
                  || 'medium').toString().toLowerCase(),
              category:
                detail.inquiryCategoryName
                || ticket.inquiryCategoryName
                || detail.category
                || ticket.category
                || 'Uncategorized',
              customFieldValues: normalizeCustomFieldValues(
                detail.customFieldValues ?? ticket.customFieldValues
              ),
            };
          }
        } catch (e) {
          console.warn('Could not load full ticket for view modal:', e);
        }
        setViewTicketModalViewOnly(Boolean(viewOnly));
        setSelectedTicketForView(ticketForModal);
        setViewTicketModalOpen(true);
        setSelectedCountryId(ticketForModal.countryId || ticket.countryId || '125');
        setSelectedCountyId(ticketForModal.countyId || ticket.countyId || '');
        setSelectedSubCountyId(ticketForModal.subCountyId || ticket.subCountyId || '');
        setSelectedWardId(ticketForModal.wardId || ticket.wardId || '');
        setRequesterUser(null);
        setAssignedUser(null);
        if (canClassifyInquiry && !viewOnly) {
          resetInquiryClassificationForm(ticketForModal);
        }
        await Promise.all([
          loadLocations(ticketForModal),
          loadUserDetails(ticketForModal),
          fetchViewModalAttachments(ticketForModal.id),
          fetchViewModalRelationsBundle(ticketForModal.id),
          ensureInquiryTypesLoaded(),
        ]);
      }
    } else {
      // For other pages, navigate to ticket detail page
      navigate(`/agent/tickets/${ticketId}`);
    }
  };

  // Load user details for requester and assigned user
  const loadUserDetails = async (ticket) => {
    const requesterId = ticket.requesterId;
    const assigneeId = ticket.assigneeId;

    if (ticket.requesterFullName) {
      setRequesterUser({
        name: formatRequesterDisplay(ticket),
        email: ticket.requesterEmail || '',
        username: null
      });
    } else if (requesterId) {
      setLoadingRequester(true);
      try {
        const requesterResponse = await userManagementService.getUserById(requesterId);
        const requesterData = requesterResponse?.data?.data || requesterResponse?.data;
        if (requesterData) {
          setRequesterUser({
            name: requesterData.fullName || `${requesterData.firstName || ''} ${requesterData.lastName || ''}`.trim() || requesterData.username || requesterData.email || 'Unknown User',
            email: requesterData.email,
            username: requesterData.username
          });
        }
      } catch (error) {
        console.error('Error fetching requester user:', error);
      } finally {
        setLoadingRequester(false);
      }
    } else if (ticket.requesterEmail) {
      setRequesterUser({
        name: formatRequesterDisplay(ticket),
        email: ticket.requesterEmail,
        username: null
      });
    } else {
      setRequesterUser(null);
    }

    if (assigneeId) {
      setLoadingAssigned(true);
      try {
        const assignedResponse = await userManagementService.getUserById(assigneeId);
        const assignedData = assignedResponse?.data?.data || assignedResponse?.data;
        if (assignedData) {
          setAssignedUser({
            name: assignedData.fullName || `${assignedData.firstName || ''} ${assignedData.lastName || ''}`.trim() || assignedData.username || assignedData.email || 'Unknown User',
            email: assignedData.email,
            username: assignedData.username
          });
        }
      } catch (error) {
        console.error('Error fetching assigned user:', error);
        // Don't show error to user, just log it
      } finally {
        setLoadingAssigned(false);
      }
    }
  };

  const fetchViewModalRelationsBundle = async (ticketId) => {
    if (!ticketRelationsEnabled || !ticketId) {
      setViewModalRelationsBundle(null);
      return;
    }
    setViewModalRelationsLoading(true);
    try {
      const res = await ticketService.getRelationsBundle(ticketId);
      const apiData = res?.data;
      if (apiData?.success === false) {
        console.warn('[TicketQueue] relations bundle responded success=false:', apiData?.message);
        setViewModalRelationsBundle(null);
      } else {
        setViewModalRelationsBundle(apiData?.data || null);
      }
    } catch (err) {
      const status = err?.response?.status || err?.status;
      if (status === 404) {
        console.debug('[TicketQueue] relations endpoint returned 404; feature likely off');
      } else {
        console.warn('[TicketQueue] failed to load relations bundle:', err?.message || err);
      }
      setViewModalRelationsBundle(null);
    } finally {
      setViewModalRelationsLoading(false);
    }
  };

  const fetchViewModalAttachments = async (ticketId) => {
    if (!ticketId) {
      setViewModalAttachments([]);
      return;
    }
    setLoadingViewModalAttachments(true);
    try {
      const response = await ticketService.getAttachments(ticketId);
      const responseData = response?.data;
      let attachmentsArray = [];
      if (responseData?.data && Array.isArray(responseData.data)) {
        attachmentsArray = responseData.data;
      } else if (Array.isArray(responseData)) {
        attachmentsArray = responseData;
      }
      setViewModalAttachments(attachmentsArray);
    } catch (err) {
      console.warn('Failed to load attachments for view modal:', err);
      setViewModalAttachments([]);
    } finally {
      setLoadingViewModalAttachments(false);
    }
  };

  const handleViewModalAttachment = (attachment) => {
    const url = resolveAttachmentOpenUrl(attachment);
    if (url) {
      window.open(url, '_blank', 'noopener,noreferrer');
    } else {
      toast.error('File path not available');
    }
  };

  const handleDownloadViewModalAttachment = (attachment) => {
    const url = resolveAttachmentOpenUrl(attachment);
    if (!url) {
      toast.error('File path not available');
      return;
    }
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', attachment.name || attachment.fileName || 'attachment');
    link.setAttribute('target', '_blank');
    link.setAttribute('rel', 'noopener noreferrer');
    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  const viewModalDuplicateLinkMode = viewModalRelationsBundle?.duplicateOf
    ? 'pick-canonical'
    : 'pick-duplicate';

  const canShowViewModalDuplicateLinkAction = useMemo(() => {
    if (!canDuplicateLink || !selectedTicketForView) return false;
    return !DUPLICATE_LINK_BLOCKED_STATUS_CODES.has(
      normalizeTicketStatusCode(selectedTicketForView)
    );
  }, [canDuplicateLink, selectedTicketForView]);

  const canCloseViewModalAsDuplicate = useMemo(
    () => canShowCloseAsDuplicateAction({
      ticketRelationsEnabled,
      duplicateOf: viewModalRelationsBundle?.duplicateOf,
      ticket: selectedTicketForView,
      allowInteraction: !viewTicketModalViewOnly,
    }),
    [
      ticketRelationsEnabled,
      viewModalRelationsBundle?.duplicateOf,
      selectedTicketForView,
      viewTicketModalViewOnly,
    ]
  );

  const applyViewModalTicketDetail = (detail) => {
    if (!detail || typeof detail !== 'object') return;
    setSelectedTicketForView((prev) => mergeViewModalTicketDetail(prev, detail));
  };

  const syncQueueTicketStatus = (ticketId, detail) => {
    if (!ticketId || !detail) return;
    const statusCode = getTicketStatusCode(detail);
    const statusDisplay =
      (typeof detail.status === 'string' && detail.status.trim() ? detail.status : null)
      || detail.statusName
      || statusCode;
    setQueueData((prev) => {
      if (!prev?.ticketQueue) return prev;
      return {
        ...prev,
        ticketQueue: prev.ticketQueue.map((ticket) => (
          Number(ticket.id) === Number(ticketId)
            ? { ...ticket, status: statusDisplay, statusCode }
            : ticket
        )),
      };
    });
  };

  const refreshSelectedTicketForView = async (ticketId) => {
    if (!ticketId) return null;
    try {
      const detailRes = await ticketService.getTicketById(ticketId);
      const detail = extractTicketDetailPayload(detailRes);
      if (detail) {
        applyViewModalTicketDetail(detail);
        syncQueueTicketStatus(ticketId, detail);
      }
      return detail;
    } catch (e) {
      console.warn('Could not refresh ticket for view modal:', e);
      return null;
    }
  };

  const handleViewModalDuplicateLinked = async () => {
    toast.success('Duplicate ticket linked successfully');
    if (selectedTicketForView?.id) {
      await fetchViewModalRelationsBundle(selectedTicketForView.id);
    }
  };

  const exitDuplicateSelectMode = () => {
    setDuplicateSelectMode(false);
    setSelectedDuplicateTicketId(null);
    setShowQueueDuplicateModal(false);
  };

  const handleQueueDuplicateLinked = () => {
    toast.success('Duplicate ticket linked successfully');
    exitDuplicateSelectMode();
    handleRefresh();
  };

  const handleOpenViewModalCloseAsDuplicate = () => {
    setCloseAsDuplicateReason('');
    setCloseAsDuplicateError('');
    setShowCloseAsDuplicateModal(true);
  };

  const handleViewModalCloseAsDuplicate = async () => {
    const trimmed = closeAsDuplicateReason.trim();
    if (!trimmed) {
      setCloseAsDuplicateError('Reason is required');
      return;
    }
    const ticketId = selectedTicketForView?.id;
    if (!ticketId || closingAsDuplicate) return;

    setCloseAsDuplicateError('');
    setClosingAsDuplicate(true);
    try {
      const response = await ticketService.closeAsDuplicate(ticketId, trimmed);
      if (!isTicketApiSuccess(response)) {
        setCloseAsDuplicateError(extractErrorMessage(response?.data, 'Failed to close ticket as duplicate'));
        return;
      }

      toast.success('Ticket closed as duplicate successfully.');
      setShowCloseAsDuplicateModal(false);
      setCloseAsDuplicateReason('');

      // Immediate UI sync — close is committed; must not wait for Update.
      setSelectedTicketForView((prev) => (
        prev
          ? {
            ...prev,
            status: 'Closed',
            statusCode: 'closed',
            statusName: 'Closed',
            closedAt: prev.closedAt ?? new Date().toISOString(),
          }
          : prev
      ));

      const closedTicket = extractTicketDetailPayload(response);
      if (closedTicket) {
        applyViewModalTicketDetail(closedTicket);
        syncQueueTicketStatus(ticketId, closedTicket);
      } else {
        await refreshSelectedTicketForView(ticketId);
      }
      await fetchViewModalRelationsBundle(ticketId);
    } catch (error) {
      setCloseAsDuplicateError(extractErrorMessage(error, 'Failed to close ticket as duplicate'));
    } finally {
      setClosingAsDuplicate(false);
    }
  };

  const closeViewTicketModal = () => {
    setViewTicketModalOpen(false);
    setViewTicketModalViewOnly(false);
    setSelectedTicketForView(null);
    setViewModalAttachments([]);
    setLoadingViewModalAttachments(false);
    setViewModalRelationsBundle(null);
    setViewModalRelationsLoading(false);
    setShowAttachDuplicateModal(false);
    setShowCloseAsDuplicateModal(false);
    setCloseAsDuplicateReason('');
    setCloseAsDuplicateError('');
    setClosingAsDuplicate(false);
    setDraftInquiryTypeId('');
    setDraftInquiryCategoryId('');
    setDraftInquirySubCategoryId('');
    setDraftInquiryPriorityLevelId('');
    setInquiryCategories([]);
    setInquirySubCategories([]);
    // Reset location states
    setSelectedCountryId('');
    setSelectedCountyId('');
    setSelectedSubCountyId('');
    setSelectedWardId('');
    setCounties([]);
    setSubCounties([]);
    setWards([]);
    // Reset user states
    setRequesterUser(null);
    setAssignedUser(null);
  };

  const resetInquiryDraftFromTicket = (ticket) => {
    setDraftInquiryTypeId(ticket?.inquiryTypeId != null ? String(ticket.inquiryTypeId) : '');
    setDraftInquiryCategoryId(ticket?.inquiryCategoryId != null ? String(ticket.inquiryCategoryId) : '');
    setDraftInquirySubCategoryId(ticket?.inquirySubCategoryId != null ? String(ticket.inquirySubCategoryId) : '');
    setDraftInquiryPriorityLevelId(ticket?.inquiryPriorityLevelId != null ? String(ticket.inquiryPriorityLevelId) : '');
  };

  /** Inquiry Type master is static; fetched once so Edit opens with a populated form. */
  const ensureInquiryTypesLoaded = async () => {
    if (!canClassifyInquiry || inquiryTypes.length > 0) return;
    try {
      const types = await ticketService.getInquiryTypesForCreate();
      setInquiryTypes(Array.isArray(types) ? types : []);
    } catch (error) {
      console.warn('Failed to load Inquiry Types:', error);
    }
  };

  const loadInquiryCategories = async (inquiryTypeId) => {
    if (inquiryTypeId == null || inquiryTypeId === '') {
      setInquiryCategories([]);
      return;
    }
    try {
      const rows = await ticketService.getInquiryCategoriesForCreate(Number(inquiryTypeId));
      setInquiryCategories(Array.isArray(rows) ? rows : []);
    } catch (error) {
      console.error('Failed to load inquiry categories:', error);
      setInquiryCategories([]);
      toast.error(extractErrorMessage(error, 'Failed to load Inquiry Categories'));
    }
  };

  const loadInquirySubCategories = async (inquiryCategoryId) => {
    if (inquiryCategoryId == null || inquiryCategoryId === '') {
      setInquirySubCategories([]);
      return;
    }
    try {
      const rows = await ticketService.getInquirySubCategoriesForCreate(Number(inquiryCategoryId));
      setInquirySubCategories(Array.isArray(rows) ? rows : []);
    } catch (error) {
      console.error('Failed to load inquiry sub categories:', error);
      setInquirySubCategories([]);
      toast.error(extractErrorMessage(error, 'Failed to load Inquiry Sub Categories'));
    }
  };

  /** Seeds the always-visible classification dropdowns from the ticket's saved values. */
  const resetInquiryClassificationForm = (ticket) => {
    resetInquiryDraftFromTicket(ticket);
    loadInquiryCategories(ticket?.inquiryTypeId);
    loadInquirySubCategories(ticket?.inquiryCategoryId);
  };

  const handleDraftInquiryTypeChange = (value) => {
    setDraftInquiryTypeId(value);
    setDraftInquiryCategoryId('');
    setDraftInquirySubCategoryId('');
    setInquirySubCategories([]);
    loadInquiryCategories(value);
  };

  const handleDraftInquiryCategoryChange = (value) => {
    setDraftInquiryCategoryId(value);
    setDraftInquirySubCategoryId('');
    loadInquirySubCategories(value);
  };

  const cancelInquiryClassificationEdit = () => {
    resetInquiryClassificationForm(selectedTicketForView);
  };

  const resolveInquiryPriorityLevelIdForSave = (ticket) => {
    if (ticket?.inquiryPriorityLevelId != null) {
      return Number(ticket.inquiryPriorityLevelId);
    }
    const notPrioritised = inquiryPriorityLevels.find((p) => {
      const code = String(p.priorityLevelCode || p.code || '').toLowerCase().replace(/[\s-]+/g, '_');
      const name = String(p.priorityLevelName || p.name || '').toLowerCase();
      return code === 'not_prioritised' || name === 'not prioritised';
    });
    return notPrioritised?.id != null ? Number(notPrioritised.id) : null;
  };

  const saveInquiryClassification = async () => {
    if (!selectedTicketForView?.id) return;
    if (!draftInquiryTypeId || !draftInquiryCategoryId || !draftInquirySubCategoryId) {
      toast.error('Select Inquiry Type, Category and Sub Category');
      return;
    }
    const inquiryPriorityLevelId = resolveInquiryPriorityLevelIdForSave(selectedTicketForView);
    if (inquiryPriorityLevelId == null) {
      toast.error('Inquiry Priority could not be resolved. Please refresh and try again.');
      return;
    }
    setSavingInquiryClassification(true);
    try {
      const response = await ticketService.updateTicket(selectedTicketForView.id, {
        inquiryTypeId: Number(draftInquiryTypeId),
        inquiryCategoryId: Number(draftInquiryCategoryId),
        inquirySubCategoryId: Number(draftInquirySubCategoryId),
        inquiryPriorityLevelId,
      });
      const updated = response?.data?.data ?? response?.data ?? response;
      const ok = response?.data?.success || response?.data?.status === 200 || updated?.ticketId != null || updated?.id != null;
      if (!ok && !updated?.inquiryTypeId) {
        toast.error(response?.data?.message || 'Failed to save Inquiry classification');
        return;
      }

      const typeName = inquiryTypes.find((t) => String(t.id) === draftInquiryTypeId)?.inquiryTypeName
        || updated?.inquiryTypeName;
      const catName = inquiryCategories.find((c) => String(c.id) === draftInquiryCategoryId)?.inquiryCategoryName
        || updated?.inquiryCategoryName;
      const subName = inquirySubCategories.find((s) => String(s.id) === draftInquirySubCategoryId)?.inquirySubCategoryName
        || updated?.inquirySubCategoryName;
      const priName = inquiryPriorityLevels.find((p) => Number(p.id) === inquiryPriorityLevelId)?.priorityLevelName
        || selectedTicketForView?.inquiryPriorityLevelName
        || updated?.inquiryPriorityLevelName;

      setSelectedTicketForView((prev) => ({
        ...prev,
        inquiryTypeId: Number(draftInquiryTypeId),
        inquiryCategoryId: Number(draftInquiryCategoryId),
        inquirySubCategoryId: Number(draftInquirySubCategoryId),
        inquiryPriorityLevelId,
        inquiryTypeName: typeName || prev?.inquiryTypeName,
        inquiryCategoryName: catName || prev?.inquiryCategoryName,
        inquirySubCategoryName: subName || prev?.inquirySubCategoryName,
        inquiryPriorityLevelName: priName || prev?.inquiryPriorityLevelName,
        // Keep assignee / SLA fields from refresh below — do not invent them.
        assigneeId: updated?.assigneeId ?? prev?.assigneeId ?? null,
        updatedAt: updated?.updatedAt || new Date().toISOString(),
      }));
      toast.success('Inquiry classification saved');

      // Refresh from server so names/IDs match persisted state; assignee must stay Unassigned.
      try {
        const fresh = await ticketService.getTicketById(selectedTicketForView.id);
        const data = fresh?.data?.data ?? fresh?.data ?? fresh;
        if (data) {
          setSelectedTicketForView((prev) => ({
            ...prev,
            ...data,
            id: data.ticketId ?? data.id ?? prev.id,
          }));
        }
      } catch (refreshError) {
        console.warn('Classification saved but ticket refresh failed:', refreshError);
      }
      await fetchQueueData();
    } catch (error) {
      console.error('Error saving inquiry classification:', error);
      toast.error(extractErrorMessage(error, 'Failed to save Inquiry classification'));
    } finally {
      setSavingInquiryClassification(false);
    }
  };

  // Location loading functions
  const loadLocations = async (ticket = null) => {
    // Load countries
    setLoadingLocations(prev => ({ ...prev, countries: true }));
    try {
      const response = await locationService.getActiveCountries();
      let countriesData = [];
      if (response?.data?.data?.data) {
        countriesData = response.data.data.data;
      } else if (response?.data?.data) {
        countriesData = response.data.data;
      } else if (response?.data) {
        countriesData = response.data;
      } else if (Array.isArray(response)) {
        countriesData = response;
      }
      setCountries(Array.isArray(countriesData) ? countriesData : []);
    } catch (error) {
      console.error('Error loading countries:', error);
      setCountries([]);
    } finally {
      setLoadingLocations(prev => ({ ...prev, countries: false }));
    }

    // If ticket has countryId, load counties
    if (ticket?.countryId) {
      await loadCounties(ticket.countryId);
    } else {
      loadCounties('125');
    }

    // If ticket has countyId, load subCounties
    if (ticket?.countyId) {
      await loadSubCounties(ticket.countyId);
    }

    // If ticket has subCountyId, load wards
    if (ticket?.subCountyId) {
      await loadWards(ticket.subCountyId);
    }
  };

  const loadCounties = async (countryId) => {
    if (!countryId) {
      setCounties([]);
      return;
    }
    setLoadingLocations(prev => ({ ...prev, counties: true }));
    try {
      const response = await locationService.getActiveCounties(countryId);
      let countiesData = [];
      if (response?.data?.data?.data) {
        countiesData = response.data.data.data;
      } else if (response?.data?.data) {
        countiesData = response.data.data;
      } else if (response?.data) {
        countiesData = response.data;
      } else if (Array.isArray(response)) {
        countiesData = response;
      }
      setCounties(Array.isArray(countiesData) ? countiesData : []);
    } catch (error) {
      console.error('Error loading counties:', error);
      setCounties([]);
    } finally {
      setLoadingLocations(prev => ({ ...prev, counties: false }));
    }
  };

  const loadSubCounties = async (countyId) => {
    if (!countyId) {
      setSubCounties([]);
      return;
    }
    setLoadingLocations(prev => ({ ...prev, subCounties: true }));
    try {
      const response = await locationService.getActiveSubCounties(countyId);
      let subCountiesData = [];
      if (response?.data?.data?.data) {
        subCountiesData = response.data.data.data;
      } else if (response?.data?.data) {
        subCountiesData = response.data.data;
      } else if (response?.data) {
        subCountiesData = response.data;
      } else if (Array.isArray(response)) {
        subCountiesData = response;
      }
      setSubCounties(Array.isArray(subCountiesData) ? subCountiesData : []);
    } catch (error) {
      console.error('Error loading sub counties:', error);
      setSubCounties([]);
    } finally {
      setLoadingLocations(prev => ({ ...prev, subCounties: false }));
    }
  };

  const loadWards = async (subCountyId) => {
    if (!subCountyId) {
      setWards([]);
      return;
    }
    setLoadingLocations(prev => ({ ...prev, wards: true }));
    try {
      const response = await locationService.getActiveWards(subCountyId);
      let wardsData = [];
      if (response?.data?.data?.data) {
        wardsData = response.data.data.data;
      } else if (response?.data?.data) {
        wardsData = response.data.data;
      } else if (response?.data) {
        wardsData = response.data;
      } else if (Array.isArray(response)) {
        wardsData = response;
      }
      setWards(Array.isArray(wardsData) ? wardsData : []);
    } catch (error) {
      console.error('Error loading wards:', error);
      setWards([]);
    } finally {
      setLoadingLocations(prev => ({ ...prev, wards: false }));
    }
  };

  // Handle location changes
  const handleCountryChange = async (e) => {
    const countryId = e.target.value;
    setSelectedCountryId(countryId);
    setSelectedCountyId('');
    setSelectedSubCountyId('');
    setSelectedWardId('');
    setCounties([]);
    setSubCounties([]);
    setWards([]);
    if (countryId) {
      await loadCounties(parseInt(countryId));
    }
  };

  const handleCountyChange = async (e) => {
    const countyId = e.target.value;
    setSelectedCountyId(countyId);
    setSelectedSubCountyId('');
    setSelectedWardId('');
    setSubCounties([]);
    setWards([]);
    if (countyId) {
      await loadSubCounties(parseInt(countyId));
    }
  };

  const handleSubCountyChange = async (e) => {
    const subCountyId = e.target.value;
    setSelectedSubCountyId(subCountyId);
    setSelectedWardId('');
    setWards([]);
    if (subCountyId) {
      await loadWards(parseInt(subCountyId));
    }
  };

  const handleWardChange = (e) => {
    setSelectedWardId(e.target.value);
  };

  // Handle location update
  const handleLocationUpdate = async () => {
    if (!selectedTicketForView) return;

    setUpdatingLocation(true);
    try {
      const updateData = {};
      if (selectedCountryId) updateData.countryId = parseInt(selectedCountryId);
      if (selectedCountyId) updateData.countyId = parseInt(selectedCountyId);
      if (selectedSubCountyId) updateData.subCountyId = parseInt(selectedSubCountyId);
      if (selectedWardId) updateData.wardId = parseInt(selectedWardId);

      const response = await ticketService.updateTicket(selectedTicketForView.id, updateData);

      if (response?.data?.success || response?.data?.status === 200) {
        toast.success('Ticket location updated successfully');

        // Find location names
        const country = countries.find(c => (c.id || c.countryId)?.toString() === selectedCountryId?.toString());
        const county = counties.find(c => (c.id || c.countyId)?.toString() === selectedCountyId?.toString());
        const subCounty = subCounties.find(sc => (sc.id || sc.subCountyId)?.toString() === selectedSubCountyId?.toString());
        const ward = wards.find(w => (w.id || w.wardId)?.toString() === selectedWardId?.toString());

        // Update the selected ticket's location in the modal
        if (selectedTicketForView) {
          setSelectedTicketForView({
            ...selectedTicketForView,
            countryId: selectedCountryId ? parseInt(selectedCountryId) : null,
            countyId: selectedCountyId ? parseInt(selectedCountyId) : null,
            subCountyId: selectedSubCountyId ? parseInt(selectedSubCountyId) : null,
            wardId: selectedWardId ? parseInt(selectedWardId) : null,
            countryName: country?.name || country?.countryName || null,
            countyName: county?.name || county?.countyName || null,
            subCountyName: subCounty?.name || subCounty?.subCountyName || null,
            wardName: ward?.name || ward?.wardName || null,
            updatedAt: new Date().toISOString()
          });
        }

        // Refresh the queue data to reflect the change
        await fetchQueueData();
      } else {
        toast.error(response?.data?.message || 'Failed to update ticket location');
      }
    } catch (error) {
      console.error('Error updating ticket location:', error);
      toast.error(extractErrorMessage(error, 'Failed to update ticket location'));
    } finally {
      setUpdatingLocation(false);
    }
  };

  const loadTeamsAndAgents = async () => {
    //console.log('🚀 loadTeamsAndAgents called');
    setLoadingTeams(true);
    setAgents([]);
    setLoadingAgents(false);

    // Get current logged-in user to exclude from teams and agents lists
    const currentUser = authService.getCurrentUser();
    const currentUserId = currentUser?.userId || currentUser?.id;

    try {
      // Load teams from backend - exclude teams where current user is a member
      //console.log('🔍 Loading teams with params:', { page: 0, size: 100, isActive: true, excludeUserId: currentUserId });
      //console.log('🔍 teamService:', teamService);
      const teamsResponse = await teamService.getTeams({
        page: 0,
        size: 100,
        isActive: true,
        excludeUserId: currentUserId // Filter out teams where current user is a member
      });
      //console.log('📦 Teams API Response:', teamsResponse);

      const teamsData = teamsResponse?.data;
      //console.log('📋 Teams Data:', teamsData);

      let teamsList = [];

      // Handle ResponseBean format: { status, message, data: PaginatedResponse { content: [...] } }
      if (teamsData?.data?.content && Array.isArray(teamsData.data.content)) {
        // PaginatedResponse format: ResponseBean.data = PaginatedResponse { content: [...] }
        teamsList = teamsData.data.content;
        //console.log('✅ Parsed teams from PaginatedResponse.content:', teamsList.length, 'teams');
      } else if (teamsData?.data && Array.isArray(teamsData.data)) {
        // Direct array in data field
        teamsList = teamsData.data;
        //console.log('✅ Parsed teams from direct array:', teamsList.length, 'teams');
      } else if (teamsData?.content && Array.isArray(teamsData.content)) {
        // PaginatedResponse without ResponseBean wrapper
        teamsList = teamsData.content;
        //console.log('✅ Parsed teams from content array:', teamsList.length, 'teams');
      } else if (Array.isArray(teamsData)) {
        // Direct array response
        teamsList = teamsData;
        //console.log('✅ Parsed teams from root array:', teamsList.length, 'teams');
      } else {
        console.warn('⚠️ Unexpected teams response structure:', teamsData);
        teamsList = [];
      }

      //console.log('📋 Final teams list:', teamsList);

      // Filter out teams where the current user is the team lead
      const filteredTeamsList = teamsList.filter(team => {
        if (currentUserId) {
          const teamLeadId = team.teamLeadId || team.team_lead_id;
          // Exclude teams where current user is the team lead
          if (teamLeadId && teamLeadId.toString() === currentUserId.toString()) {
            return false;
          }
        }
        return true;
      });

      setTeams(filteredTeamsList);
    } catch (error) {
      console.error('❌ Error loading teams:', error);
      console.error('❌ Error details:', {
        message: error?.message,
        response: error?.response,
        stack: error?.stack
      });
      toast.error(extractErrorMessage(error, 'Failed to load teams'));
      setTeams([]);
    } finally {
      setLoadingTeams(false);
    }
  };

  const loadAgentsForAssignPriority = async (priorityLevelId, ticket = currentTicket) => {
    setSelectedAgent('');
    setAgents([]);
    setAssignResponsibleRoleName('');

    if (!priorityLevelId || assignMode !== 'agent') {
      setLoadingAgents(false);
      return;
    }

    const categoryId = ticket?.inquiryCategoryId;
    const subCategoryId = ticket?.inquirySubCategoryId;
    if (!categoryId || !subCategoryId) {
      toast.error('Inquiry Category / Sub Category missing on this ticket');
      setLoadingAgents(false);
      return;
    }

    setLoadingAgents(true);
    try {
      const role = await ticketService.getLevel1ResponsibleRoleForAssign(
        categoryId,
        subCategoryId,
        Number(priorityLevelId)
      );
      const roleId = role?.responsibleRoleId;
      setAssignResponsibleRoleName(role?.responsibleRoleName || '');
      if (!roleId) {
        toast.error('No Level 1 Responsible Role found for this Inquiry Priority. Check Escalation Matrix.');
        return;
      }

      const users = await userManagementService.getAssignableUsers(roleId);
      const currentUser = authService.getCurrentUser();
      const currentUserId = currentUser?.userId || currentUser?.id;
      const agentsList = users
        .map((user) => {
          const userId = user.userId || user.id;
          return {
            id: userId,
            name: userManagementService.getUserDisplayName(user),
            email: user.email,
            userId
          };
        })
        .filter((agent) => {
          if (!currentUserId) return true;
          return String(agent.userId) !== String(currentUserId);
        });

      setAgents(agentsList);
      if (agentsList.length === 0) {
        toast.error('No users found for the Responsible Role');
      }
    } catch (error) {
      console.error('Failed to load assignable users for priority:', error);
      toast.error(extractErrorMessage(error, 'Failed to load users for assignment'));
      setAgents([]);
    } finally {
      setLoadingAgents(false);
    }
  };

  const handleAssignPriorityChange = async (priorityLevelId) => {
    setSelectedPriorityId(priorityLevelId);
    await loadAgentsForAssignPriority(priorityLevelId, currentTicket);
  };

  const openAssignModal = (ticket) => {
    setCurrentTicketId(ticket.id);
    setCurrentTicket(ticket); // Store full ticket object for requesterId
    setAssignModalOpen(true);
    setAssignMode(assignScopeSelfOnly ? 'me' : 'agent');
    setSelectedTeam('');
    setSelectedAgent('');
    setSelectedPriorityId('');
    setAgents([]);
    setAssignResponsibleRoleName('');
  };

  const closeAssignModal = () => {
    setAssignModalOpen(false);
    setCurrentTicketId(null);
    setCurrentTicket(null); // Clear ticket object
    setAssignMode(assignScopeSelfOnly ? 'me' : 'agent');
    setSelectedTeam('');
    setSelectedAgent('');
    setSelectedPriorityId('');
    setAgents([]);
    setAssignResponsibleRoleName('');
    setAssignSubmitting(false);
  };

  const closeAssignConflictModal = () => {
    setAssignConflictOpen(false);
    setAssignConflictMessage('');
  };

  /** Refresh list and show dialog when another agent already owns the ticket */
  const showTicketAssignConflict = async (message) => {
    setAssignConflictMessage(message);
    setAssignConflictOpen(true);
    closeAssignModal();
    try {
      await fetchQueueData();
    } catch (e) {
      console.warn('Failed to refresh queue after assign conflict', e);
    }
  };

  /** True if ticket row already has a different assignee than target (stale UI before API) */
  const hasAssigneeConflictWithTarget = (targetAssigneeId) => {
    const raw = currentTicket?.assigneeId;
    if (raw == null || raw === '') return false;
    const existing = Number(raw);
    const target = Number(targetAssigneeId);
    if (!Number.isFinite(existing) || !Number.isFinite(target)) return false;
    return existing !== target;
  };

  const refreshAfterAssign = (assignedTicketId, { removeFromList = true } = {}) => {
    const idStr = String(assignedTicketId);
    if (removeFromList) {
      setQueueData((prev) => {
        if (!prev?.ticketQueue) return prev;
        return {
          ...prev,
          ticketQueue: prev.ticketQueue.filter(
            (t) => String(t.id) !== idStr && String(t.ticketId) !== idStr
          )
        };
      });
      setTotalElements((prev) => Math.max(0, (prev || 1) - 1));
    }
    const apiPage = currentPage - 1;
    void fetchQueueData(apiPage, 10, true, true).catch((err) => console.warn('Post-assign queue refresh failed:', err));
  };

  const handleAssignToMe = async () => {
    if (!currentTicketId) return;

    if (!selectedPriorityId) {
      toast.error('Please select a priority');
      return;
    }

    try {
      // Get current user ID from JWT token
      const currentUser = authService.getCurrentUser();
      if (!currentUser || !currentUser.userId) {
        toast.error('Unable to get current user information');
        return;
      }

      const assigneeId = currentUser.userId;

      if (hasAssigneeConflictWithTarget(assigneeId)) {
        const label =
          typeof currentTicket?.assignedAgent === 'string' && !/^Agent\s+\d+$/i.test(currentTicket.assignedAgent)
            ? currentTicket.assignedAgent
            : `agent ID ${currentTicket.assigneeId}`;
        await showTicketAssignConflict(
          `This ticket is already assigned to ${label}. Another agent may have claimed it while you had this window open.`
        );
        return;
      }

      // Get requesterId from the current ticket for backend FCM notification
      const requesterId = currentTicket?.requesterId || null;
      const assignResponse = await ticketService.assignTicket(
        currentTicketId,
        assigneeId,
        requesterId,
        Number(selectedPriorityId)
      );

      // Backend returns ResponseBean: { status, message, data, success }
      const responseData = assignResponse?.data;

      if (responseData?.success || responseData?.status === 200) {
        closeAssignModal();
        toast.success('Ticket assigned to you successfully');
        refreshAfterAssign(currentTicketId);
      } else {
        toast.error(responseData?.message || 'Failed to assign ticket');
      }
    } catch (error) {
      console.error('Error assigning ticket:', error);
      if (error?.status === 409) {
        await showTicketAssignConflict(
          extractErrorMessage(error, 'This ticket is already assigned to another agent.')
        );
        return;
      }
      toast.error(extractErrorMessage(error, 'Error assigning ticket'));
    }
  };

  const handleAssignToTeam = async () => {
    if (!currentTicketId || !selectedTeam) {
      toast.error('Please select a team');
      return;
    }

    try {
      const team = teams.find(t => (t.teamId || t.id)?.toString() === selectedTeam.toString());
      if (!team) {
        toast.error('Selected team not found');
        return;
      }

      // Update ticket with teamId (not assigneeId) - backend should handle team assignment
      // The ticket should be assigned to the team, not to a specific user
      const teamId = team.teamId || team.id;
      const requesterId = currentTicket?.requesterId || null;

      // Update ticket with teamId - backend should set teamId field
      const updateResponse = await ticketService.updateTicket(currentTicketId, {
        teamId: teamId
      });

      const responseData = updateResponse?.data;

      if (responseData?.success || responseData?.status === 200) {
        closeAssignModal();
        toast.success(`Ticket assigned to team "${team.teamName || team.name}" successfully`);
        refreshAfterAssign(currentTicketId, { removeFromList: false });
      } else {
        toast.error(responseData?.message || 'Failed to assign ticket to team');
      }
    } catch (error) {
      console.error('Error assigning ticket to team:', error);
      toast.error(extractErrorMessage(error, 'Error assigning ticket to team'));
    }
  };

  const handleAssignToAgent = async () => {
    if (!currentTicketId || !selectedAgent) {
      toast.error('Please select an agent');
      return;
    }

    if (!selectedPriorityId) {
      toast.error('Please select a priority');
      return;
    }

    try {
      // Find the agent by matching the selectedAgent value (which is userId from dropdown)
      const agent = agents.find(a => (a.userId && a.userId.toString() === selectedAgent.toString()) || (a.id && a.id.toString() === selectedAgent.toString()));

      // Always use userId as assigneeId (this is the user's ID from Users table)
      // This ensures tickets are assigned with the correct user ID for "My Tickets" queries
      const assigneeId = agent?.userId || selectedAgent;

      if (!assigneeId) {
        toast.error('Unable to determine agent ID');
        return;
      }

      if (hasAssigneeConflictWithTarget(assigneeId)) {
        const label =
          typeof currentTicket?.assignedAgent === 'string' && !/^Agent\s+\d+$/i.test(currentTicket.assignedAgent)
            ? currentTicket.assignedAgent
            : `agent ID ${currentTicket.assigneeId}`;
        await showTicketAssignConflict(
          `This ticket is already assigned to ${label}. Another agent may have claimed it while you had this window open.`
        );
        return;
      }

      // Get requesterId from the current ticket for backend FCM notification
      const requesterId = currentTicket?.requesterId || null;
      const assignResponse = await ticketService.assignTicket(
        currentTicketId,
        assigneeId,
        requesterId,
        Number(selectedPriorityId)
      );

      const responseData = assignResponse?.data;

      if (responseData?.success || responseData?.status === 200) {
        closeAssignModal();
        toast.success(`Ticket assigned to ${agent?.name || 'agent'} successfully`);
        refreshAfterAssign(currentTicketId);
      } else {
        toast.error(responseData?.message || 'Failed to assign ticket');
      }
    } catch (error) {
      console.error('Error assigning ticket:', error);
      if (error?.status === 409) {
        await showTicketAssignConflict(
          extractErrorMessage(error, 'This ticket is already assigned to another agent.')
        );
        return;
      }
      toast.error(extractErrorMessage(error, 'Error assigning ticket'));
    }
  };

  const handleUnifiedAssign = async () => {
    if (assignSubmitting) return;
    if (assignScopeSelfOnly && assignMode !== 'me') {
      toast.error('You can only assign this ticket to yourself.');
      return;
    }
    // Team transfer keeps existing priority; agent assignment requires priority
    if (assignMode !== 'team' && !selectedPriorityId) {
      toast.error('Please select a priority');
      return;
    }
    setAssignSubmitting(true);
    try {
      if (assignMode === 'me') {
        await handleAssignToMe();
      } else if (assignMode === 'team') {
        await handleAssignToTeam();
      } else {
        await handleAssignToAgent();
      }
    } finally {
      setAssignSubmitting(false);
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority?.toLowerCase()) {
      case 'critical': return 'text-red-600 dark:text-red-400 bg-red-100 dark:bg-red-900';
      case 'urgent': return 'text-red-50 dark:text-red-100 bg-red-800 dark:bg-red-950';
      case 'high': return 'text-orange-600 dark:text-orange-400 bg-orange-100 dark:bg-orange-900';
      case 'medium': return 'text-yellow-600 dark:text-yellow-400 bg-yellow-100 dark:bg-yellow-900';
      case 'low': return 'text-green-600 dark:text-green-400 bg-green-100 dark:bg-green-900';
      default: return 'text-gray-600 dark:text-gray-400 bg-gray-100 dark:bg-gray-700';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'new': return 'text-blue-600 dark:text-blue-400 bg-blue-100 dark:bg-blue-900';
      case 'open': return 'text-orange-600 dark:text-orange-400 bg-orange-100 dark:bg-orange-900';
      case 'pending': return 'text-yellow-600 dark:text-yellow-400 bg-yellow-100 dark:bg-yellow-900';
      case 'resolved': return 'text-green-600 dark:text-green-400 bg-green-100 dark:bg-green-900';
      case 'closed': return 'text-gray-600 dark:text-gray-400 bg-gray-100 dark:bg-gray-700';
      case 'reopen': return 'text-purple-600 dark:text-purple-400 bg-purple-100 dark:bg-purple-900';
      default: return 'text-gray-600 dark:text-gray-400 bg-gray-100 dark:bg-gray-700';
    }
  };

  const getSLAStatusColor = (slaStatus) => {
    switch (slaStatus) {
      case 'on-track': return 'text-green-600 dark:text-green-400';
      case 'warning': return 'text-yellow-600 dark:text-yellow-400';
      case 'breach': return 'text-red-600 dark:text-red-400';
      default: return 'text-gray-600 dark:text-gray-400';
    }
  };

  const getSLAIcon = (slaStatus) => {
    switch (slaStatus) {
      case 'on-track': return CheckCircle;
      case 'warning': return AlertTriangle;
      case 'breach': return AlertCircle;
      default: return Clock;
    }
  };

  const formatTimeRemaining = (minutes) => {
    if (minutes < 0) {
      return `${Math.abs(minutes)}m overdue`;
    } else if (minutes < 60) {
      return `${minutes}m left`;
    } else if (minutes < 1440) {
      const hours = Math.floor(minutes / 60);
      const mins = minutes % 60;
      return `${hours}h ${mins}m left`;
    } else {
      const days = Math.floor(minutes / 1440);
      const hours = Math.floor((minutes % 1440) / 60);
      return `${days}d ${hours}h left`;
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">Loading ticket queue...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <p className="text-red-600 dark:text-red-400">{error}</p>
        </div>
      </div>
    );
  }

  if (!queueData) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <p className="text-gray-600 dark:text-gray-400">No queue data available</p>
        </div>
      </div>
    );
  }

  // Handle case where required properties are undefined
  if (!queueData.ticketQueue) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <p className="text-red-600 dark:text-red-400">Ticket queue data is not available</p>
          <p className="text-gray-600 dark:text-gray-400 mt-2">Please contact your administrator</p>
        </div>
      </div>
    );
  }

  const {
    ticketQueue = [],
    queueStats = {},
    queueFilters = {},
    bulkActions = [],
    quickActions = [],
    sortOptions = []
  } = queueData;

  // Calculate SLA time remaining for tickets that don't have it
  const calculateSlaTimeRemaining = (ticket) => {
    if (ticket.slaTimeRemaining !== undefined && ticket.slaTimeRemaining !== null) {
      return ticket.slaTimeRemaining;
    }
    if (ticket.dueDate) {
      const dueDate = new Date(ticket.dueDate);
      const now = new Date();
      const diffMs = dueDate.getTime() - now.getTime();
      const diffMinutes = Math.floor(diffMs / (1000 * 60));
      return diffMinutes;
    }
    return null;
  };

  // Filter and sort tickets (Unassigned: backend is single source of truth — no client-side re-filter)
  let filteredTickets = ticketQueue.map((ticket) => ({
    ...ticket,
    slaTimeRemaining: calculateSlaTimeRemaining(ticket)
  }));

  if (title !== 'Unassigned Tickets') {
    filteredTickets = filteredTickets.filter((ticket) => {
      if (
        searchTerm &&
        !ticket.title?.toLowerCase().includes(searchTerm.toLowerCase()) &&
        !ticket.ticketNumber?.toLowerCase().includes(searchTerm.toLowerCase())
      ) {
        return false;
      }
      if (filters.priority.length > 0 && !filters.priority.includes(ticket.priority)) {
        return false;
      }
      if (filters.status.length > 0 && !filters.status.includes(ticket.status)) {
        return false;
      }
      if (filters.category.length > 0 && !filters.category.includes(ticket.category)) {
        return false;
      }
      if (filters.slaStatus.length > 0 && !filters.slaStatus.includes(ticket.slaStatus)) {
        return false;
      }
      if (filters.assigned !== null && filters.assigned !== undefined) {
        const isAssigned = ticket.assignedAgent != null;
        if (filters.assigned === true && !isAssigned) return false;
        if (filters.assigned === false && isAssigned) return false;
      }
      return true;
    });
  }

  // Sort tickets (skip for Unassigned Tickets — list is server-paginated; preserve API order)
  const sortOption = sortOptions.find(option => option.id === sortBy);
  if (sortOption && title !== 'Unassigned Tickets') {
    filteredTickets.sort((a, b) => {
      let aValue = a[sortOption.field];
      let bValue = b[sortOption.field];

      if (sortOption.field === 'priority') {
        const priorityOrder = { critical: 4, high: 3, medium: 2, low: 1, urgent: 5 };
        aValue = priorityOrder[aValue] || 0;
        bValue = priorityOrder[bValue] || 0;
      }

      if (sortOption.direction === 'asc') {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });
  }

  // Pagination
  // For unassigned tickets, use server-side pagination (no client-side slicing)
  // For other pages, use client-side pagination
  let paginatedTickets;
  let displayTotalPages;
  let displayTotalElements;

  if (title === "Unassigned Tickets") {
    // Server-side pagination: use tickets directly from API (already paginated)
    paginatedTickets = filteredTickets; // filteredTickets already contains only the current page's tickets
    displayTotalPages = totalPages || 1;
    displayTotalElements = totalElements || 0;
  } else {
    // Client-side pagination: slice the filtered tickets
    displayTotalPages = Math.ceil(filteredTickets.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    paginatedTickets = filteredTickets.slice(startIndex, endIndex);
    displayTotalElements = filteredTickets.length;
  }

  const startIndex = title === "Unassigned Tickets"
    ? (currentPage - 1) * itemsPerPage + 1
    : (currentPage - 1) * itemsPerPage;
  const endIndex = title === "Unassigned Tickets"
    ? Math.min(currentPage * itemsPerPage, displayTotalElements)
    : Math.min(startIndex + itemsPerPage, displayTotalElements);

  const isUnassignedQueue = title === 'Unassigned Tickets';

  const selectedDuplicateTicket = isUnassignedQueue && selectedDuplicateTicketId
    ? filteredTickets.find(
      (t) => Number(t.ticketId || t.id) === Number(selectedDuplicateTicketId),
    ) || null
    : null;

  const canLinkSelectedAsDuplicate = Boolean(
    selectedDuplicateTicket
    && !DUPLICATE_LINK_BLOCKED_STATUS_CODES.has(
      normalizeTicketStatusCode(selectedDuplicateTicket),
    ),
  );

  const handleDuplicateTicketAction = () => {
    if (!duplicateSelectMode) {
      setDuplicateSelectMode(true);
      setSelectedDuplicateTicketId(null);
      return;
    }
    if (!selectedDuplicateTicketId) {
      setDuplicateSelectMode(false);
      return;
    }
    if (!canLinkSelectedAsDuplicate) {
      toast.error('This ticket cannot be linked as a duplicate');
      return;
    }
    setShowQueueDuplicateModal(true);
  };

  const queueFilterSelectClass =
    'appearance-none px-2 py-1.5 pr-6 text-xs border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed w-28';

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <div className={title === "Unassigned Tickets" ? "pt-2 pb-6" : "max-w-7xl mx-auto px-2 sm:px-3 lg:px-4 pt-2 pb-6"}>
        {/* Queue Statistics — other queues only; Unassigned merges card into toolbar row below */}
        {!isUnassignedQueue && (
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-6">
            <button
              onClick={() => setFilters({ ...filters, status: [], priority: [] })}
              className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700 hover:shadow-lg transition-shadow cursor-pointer text-left"
            >
              <div className="flex items-center">
                <div className="p-2 bg-blue-100 dark:bg-blue-900 rounded-lg">
                  <Ticket className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                </div>
                <div className="ml-3">
                  <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Total</p>
                  <p className="text-xl font-semibold text-gray-900 dark:text-gray-100">{queueStats.total || 0}</p>
                </div>
              </div>
            </button>
            <button
              onClick={() => setFilters({ ...filters, status: ['new'], priority: [] })}
              className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700 hover:shadow-lg transition-shadow cursor-pointer text-left"
            >
              <div className="flex items-center">
                <div className="p-2 bg-green-100 dark:bg-green-900 rounded-lg">
                  <CheckCircle className="h-5 w-5 text-green-600 dark:text-green-400" />
                </div>
                <div className="ml-3">
                  <p className="text-sm font-medium text-gray-600 dark:text-gray-400">New</p>
                  <p className="text-xl font-semibold text-gray-900 dark:text-gray-100">{queueStats.new || 0}</p>
                </div>
              </div>
            </button>
            <button
              onClick={() => setFilters({ ...filters, status: [], priority: ['high', 'critical'] })}
              className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700 hover:shadow-lg transition-shadow cursor-pointer text-left"
            >
              <div className="flex items-center">
                <div className="p-2 bg-orange-100 dark:bg-orange-900 rounded-lg">
                  <AlertTriangle className="h-5 w-5 text-orange-600 dark:text-orange-400" />
                </div>
                <div className="ml-3">
                  <p className="text-sm font-medium text-gray-600 dark:text-gray-400">High Priority</p>
                  <p className="text-xl font-semibold text-gray-900 dark:text-gray-100">{queueStats.highPriority || 0}</p>
                </div>
              </div>
            </button>
            <button
              onClick={() => setFilters({ ...filters, status: [], priority: [], slaStatus: ['breach'] })}
              className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700 hover:shadow-lg transition-shadow cursor-pointer text-left"
            >
              <div className="flex items-center">
                <div className="p-2 bg-red-100 dark:bg-red-900 rounded-lg">
                  <AlertCircle className="h-5 w-5 text-red-600 dark:text-red-400" />
                </div>
                <div className="ml-3">
                  <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Overdue</p>
                  <p className="text-xl font-semibold text-gray-900 dark:text-gray-100">{queueStats.overdue || 0}</p>
                </div>
              </div>
            </button>
            <button
              onClick={() => setFilters({ ...filters, status: [], priority: [], assigned: false })}
              className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700 hover:shadow-lg transition-shadow cursor-pointer text-left"
            >
              <div className="flex items-center">
                <div className="p-2 bg-purple-100 dark:bg-purple-900 rounded-lg">
                  <UserPlus className="h-5 w-5 text-purple-600 dark:text-purple-400" />
                </div>
                <div className="ml-3">
                  <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Unassigned</p>
                  <p className="text-xl font-semibold text-gray-900 dark:text-gray-100">{queueStats.unassigned || 0}</p>
                </div>
              </div>
            </button>
          </div>
        )}


        {/* Search and Sort - sticky on Unassigned Tickets so Export dropdown stays visible without scrolling */}
        <div className={`mb-6 ${isUnassignedQueue ? 'sticky top-0 z-30 bg-gray-50 dark:bg-gray-900 pt-2 pb-2 -mx-2 px-2 rounded-md' : ''}`}>
          {isUnassignedQueue ? (
            <div className="flex flex-col gap-3 lg:flex-row lg:items-stretch lg:gap-4">
              <div className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700 flex-shrink-0 lg:w-64 xl:w-72">
                <div className="flex items-center">
                  <div className="p-2 bg-blue-100 dark:bg-blue-900 rounded-lg">
                    <Ticket className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                  </div>
                  <div className="ml-3 min-w-0">
                    <p className="text-sm font-medium text-gray-600 dark:text-gray-400 truncate">Total Unassigned Tickets</p>
                    <p className="text-xl font-semibold text-gray-900 dark:text-gray-100">
                      {loading ? '…' : (totalElements || 0)}
                    </p>
                  </div>
                </div>
              </div>

              <div className="flex-1 min-w-0 flex flex-col sm:flex-row sm:items-center gap-3 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-3 shadow-sm">
                <div className="relative flex-1 min-w-0">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search tickets..."
                    value={searchTerm}
                    onChange={(e) => {
                      e.preventDefault();
                      setSearchTerm(e.target.value);
                      sessionStorage.removeItem('notificationTicketNumber');
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        e.stopPropagation();
                      }
                    }}
                    className="w-full h-10 pl-10 pr-4 text-sm rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 shadow-sm placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary/40 transition-shadow"
                  />
                </div>

                <div className="flex items-center gap-2 flex-shrink-0 justify-end">
                  {canDuplicateLink && (
                    <Button
                      type="button"
                      variant="outline"
                      className={`flex items-center gap-2 h-10 shadow-sm ${
                        duplicateSelectMode
                          ? 'bg-amber-400 border-amber-500 text-amber-950 hover:bg-amber-500 dark:bg-amber-500 dark:border-amber-400 dark:text-amber-950 dark:hover:bg-amber-400'
                          : 'border-amber-300 dark:border-amber-700 text-amber-900 dark:text-amber-100 hover:bg-amber-50 dark:hover:bg-amber-900/30'
                      }`}
                      onClick={handleDuplicateTicketAction}
                    >
                      <Copy className="h-4 w-4" />
                      Duplicate Ticket
                    </Button>
                  )}
                  <Button
                    type="button"
                    variant="outline"
                    className="flex items-center gap-2 h-10 shadow-sm"
                    onClick={handleRefresh}
                    disabled={loading}
                  >
                    <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
                    Refresh
                  </Button>
                  <div className="relative flex-shrink-0">
                    <Button
                      type="button"
                      variant="outline"
                      className="flex items-center gap-2 h-10 shadow-sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        setExportMenuOpen((v) => !v);
                      }}
                      disabled={loading || exporting}
                    >
                      <Download className="h-4 w-4" />
                      <span>{exporting ? 'Exporting…' : 'Export'}</span>
                      <ChevronDown className="h-4 w-4" />
                    </Button>
                    {exportMenuOpen && (
                      <>
                        <div className="fixed inset-0 z-40" aria-hidden onClick={() => setExportMenuOpen(false)} />
                        <div className="absolute right-0 top-full mt-1 py-1 z-50 min-w-[140px] rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-lg">
                          <button
                            type="button"
                            className="w-full px-3 py-2 text-left text-sm hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2 text-gray-900 dark:text-gray-100"
                            onClick={handleExportCsv}
                          >
                            <FileText className="h-4 w-4" />
                            Export CSV
                          </button>
                          <button
                            type="button"
                            className="w-full px-3 py-2 text-left text-sm hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2 text-gray-900 dark:text-gray-100"
                            onClick={handleExportPdf}
                          >
                            <FileText className="h-4 w-4" />
                            Export PDF
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ) : (
          <div className="flex items-center justify-between gap-2 flex-nowrap">
            <div className="flex items-center gap-2 min-w-0 flex-1 flex-nowrap overflow-x-auto">
              <div className="relative flex-shrink-0">
                <Search className="absolute top-1/2 -translate-y-1/2 text-gray-400 left-2 h-4 w-4" />
                <input
                  type="text"
                  placeholder="Search tickets..."
                  value={searchTerm}
                  onChange={(e) => {
                    e.preventDefault();
                    setSearchTerm(e.target.value);
                    sessionStorage.removeItem('notificationTicketNumber');
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      e.stopPropagation();
                    }
                  }}
                  onClick={() => setShowFilters(true)}
                  className="pl-8 pr-3 py-1.5 text-xs border border-gray-300 dark:border-gray-600 rounded-md bg-transparent text-gray-900 dark:text-gray-100 w-40 cursor-pointer"
                />
              </div>

              {showFilters && (
                <div className="contents">
                  {/* Priority Dropdown */}
                  <div className="relative flex-shrink-0">
                    <select
                      value={filters.priority.length > 0 ? filters.priority[0] : 'all'}
                      onChange={(e) => {
                        if (e.target.value === 'all') {
                          setFilters(prev => ({ ...prev, priority: [], priorityId: null }));
                        } else {
                          const p = findPriorityByToken(e.target.value, priorities);
                          setFilters(prev => ({
                            ...prev,
                            priority: [e.target.value],
                            priorityId: p?.priorityId ?? null
                          }));
                        }
                      }}
                      disabled={priorities.length === 0}
                      className={queueFilterSelectClass}
                    >
                      <option value="all">All Priority</option>
                      {priorities.map(priority => {
                        const priorityCode = (priority.priorityCode || priority.priorityName || '').toLowerCase();
                        return (
                          <option key={priority.priorityId || priorityCode} value={priorityCode}>
                            {priority.priorityName || priority.priorityCode || priorityCode}
                          </option>
                        );
                      })}
                    </select>
                    <ChevronDown className="absolute right-1.5 top-1/2 transform -translate-y-1/2 h-3 w-3 text-gray-400 pointer-events-none" />
                  </div>

                  {/* Status Dropdown */}
                  <div className="relative flex-shrink-0">
                    <select
                      value={filters.status.length > 0 ? filters.status[0] : 'all'}
                      onChange={(e) => {
                        if (e.target.value === 'all') {
                          setFilters(prev => ({ ...prev, status: [] }));
                        } else {
                          setFilters(prev => ({ ...prev, status: [e.target.value] }));
                        }
                      }}
                      disabled={statuses.length === 0}
                      className="appearance-none px-2 py-1.5 pr-6 text-xs border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed w-24"
                    >
                      <option value="all">All Status</option>
                      {statuses.map(status => {
                        const statusCode = status.statusCode || status.statusName?.toLowerCase() || '';
                        return (
                          <option key={status.statusId || statusCode} value={statusCode}>
                            {status.statusName || status.statusCode}
                          </option>
                        );
                      })}
                    </select>
                    <ChevronDown className="absolute right-1.5 top-1/2 transform -translate-y-1/2 h-3 w-3 text-gray-400 pointer-events-none" />
                  </div>

                  {/* Category Dropdown */}
                  <div className="relative flex-shrink-0">
                    <select
                      value={filters.category.length > 0 ? filters.category[0] : 'all'}
                      onChange={(e) => {
                        if (e.target.value === 'all') {
                          setFilters(prev => ({ ...prev, category: [] }));
                        } else {
                          setFilters(prev => ({ ...prev, category: [e.target.value] }));
                        }
                      }}
                      disabled={filterableCategories.length === 0}
                      className={queueFilterSelectClass}
                    >
                      <option value="all">All Category</option>
                      {filterableCategories.map((category) => {
                        const categoryName = category.categoryName || category.name || '';
                        return (
                          <option key={category.categoryId} value={categoryName}>
                            {categoryName || 'Uncategorized'}
                          </option>
                        );
                      })}
                    </select>
                    <ChevronDown className="absolute right-1.5 top-1/2 transform -translate-y-1/2 h-3 w-3 text-gray-400 pointer-events-none" />
                  </div>

                  {/* SLA Status Dropdown */}
                  <div className="relative flex-shrink-0">
                    <select
                      value={filters.slaStatus.length > 0 ? filters.slaStatus[0] : 'all'}
                      onChange={(e) => {
                        if (e.target.value === 'all') {
                          setFilters(prev => ({ ...prev, slaStatus: [] }));
                        } else {
                          setFilters(prev => ({ ...prev, slaStatus: [e.target.value] }));
                        }
                      }}
                      disabled={!queueData?.queueFilters?.slaStatuses}
                      className={queueFilterSelectClass}
                    >
                      <option value="all">All SLA Status</option>
                      {queueData?.queueFilters?.slaStatuses?.map(slaStatus => (
                        <option key={slaStatus} value={slaStatus}>{slaStatus === 'on-track' ? 'On-Track' : slaStatus.charAt(0).toUpperCase() + slaStatus.slice(1)}</option>
                      ))}
                    </select>
                    <ChevronDown className="absolute right-1.5 top-1/2 transform -translate-y-1/2 h-3 w-3 text-gray-400 pointer-events-none" />
                  </div>

                  {/* Assignment Dropdown */}
                  <div className="relative flex-shrink-0">
                    <select
                      value={filters.assigned === true ? 'assigned' : filters.assigned === false ? 'unassigned' : 'all'}
                      onChange={(e) => {
                        if (e.target.value === 'all') {
                          setFilters(prev => ({ ...prev, assigned: null }));
                        } else if (e.target.value === 'assigned') {
                          setFilters(prev => ({ ...prev, assigned: true }));
                        } else {
                          setFilters(prev => ({ ...prev, assigned: false }));
                        }
                      }}
                      className="appearance-none px-2 py-1.5 pr-6 text-xs border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-1 focus:ring-blue-500 w-28"
                    >
                      <option value="all">All Assignment</option>
                      <option value="assigned">Assigned</option>
                      <option value="unassigned">Unassigned</option>
                    </select>
                    <ChevronDown className="absolute right-1.5 top-1/2 transform -translate-y-1/2 h-3 w-3 text-gray-400 pointer-events-none" />
                  </div>

                  {title !== 'Unassigned Tickets' && (
                    <select
                      value={sortBy}
                      onChange={(e) => setSortBy(e.target.value)}
                      className="px-2 py-1.5 text-xs border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 flex-shrink-0 w-36"
                    >
                      {sortOptions && sortOptions.length > 0 ? sortOptions.map(option => (
                        <option key={option.id} value={option.id}>{option.name}</option>
                      )) : (
                        <option value="">No sort options available</option>
                      )}
                    </select>
                  )}
                </div>
              )}
            </div>
            <div className="flex items-center space-x-2">
              <span className="text-sm text-gray-600 dark:text-gray-400">
                {filteredTickets.length} tickets
              </span>
              <button
                onClick={() => setShowFilters(!showFilters)}
                className="flex items-center space-x-2 px-3 py-2 text-sm font-medium text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 border border-gray-300 dark:border-gray-600 rounded-md transition-colors"
              >
                <Filter className="h-4 w-4" />
                <span>Filter</span>
              </button>
              <button
                onClick={handleRefresh}
                disabled={loading}
                className="flex items-center space-x-2 px-3 py-2 text-sm font-medium text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 border border-gray-300 dark:border-gray-600 rounded-md transition-colors disabled:opacity-50"
              >
                <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
                <span>Refresh</span>
              </button>
              {selectedTickets.length > 0 && (
                <button
                  onClick={() => setShowBulkActions(!showBulkActions)}
                  className="px-3 py-1 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700"
                >
                  {selectedTickets.length} selected
                </button>
              )}
            </div>
          </div>
          )}
        </div>

        {/* Bulk Actions */}
        {showBulkActions && selectedTickets.length > 0 && (
          <div className="bg-blue-50 dark:bg-blue-900 rounded-lg border border-blue-200 dark:border-blue-700 p-4 mb-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <span className="text-sm font-medium text-blue-800 dark:text-blue-200">
                  {selectedTickets.length} tickets selected
                </span>
                <div className="flex items-center space-x-2">
                  {bulkActions && bulkActions.length > 0 ? bulkActions.map(action => (
                    <button
                      key={action.id}
                      onClick={() => handleBulkAction(action.id)}
                      className="flex items-center space-x-1 px-3 py-1 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700"
                    >
                      <span>{action.name}</span>
                    </button>
                  )) : (
                    <div className="text-sm text-gray-500 dark:text-gray-400">No bulk actions available</div>
                  )}
                </div>
              </div>
              <button
                onClick={() => {
                  setSelectedTickets([]);
                  setShowBulkActions(false);
                }}
                className="text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>
        )}

        {/* Tickets Table */}
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden relative">
          {/* Search Loading Indicator - Small progress bar at top of table */}
          {searchLoading && (
            <div className="absolute top-0 left-0 right-0 h-1 bg-gray-200 dark:bg-gray-700 z-10">
              <div className="h-full bg-blue-600 animate-pulse" style={{ width: '30%' }}></div>
            </div>
          )}
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-700">
                <tr>
                  {/* <th className="px-6 py-3 text-left">
                    <input
                      type="checkbox"
                      checked={selectedTickets.length === filteredTickets.length && filteredTickets.length > 0}
                      onChange={handleSelectAll}
                      className="rounded border-gray-300 dark:border-gray-600"
                    />
                  </th> */}
                  {isUnassignedQueue && canDuplicateLink && duplicateSelectMode && (
                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider w-10">
                      <span className="sr-only">Select</span>
                    </th>
                  )}
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    S.No
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Ticket
                  </th>
                  {!isUnassignedQueue && (
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Priority
                    </th>
                  )}
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Assigned
                  </th>
                  {title !== "Unassigned Tickets" && (
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      SLA Status
                    </th>
                  )}
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Created
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    {isUnassignedQueue ? 'Action' : 'Actions'}
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                {paginatedTickets.map((ticket, index) => {
                  const SLAIcon = title !== "Unassigned Tickets" ? getSLAIcon(ticket.slaStatus) : null;
                  const isReopened = ticket.status?.toLowerCase() === 'reopen';
                  const rowTicketId = ticket.ticketId || ticket.id;
                  return (
                    <tr
                      key={ticket.id}
                      className={`hover:bg-gray-50 dark:hover:bg-gray-700 ${isReopened ? 'bg-purple-50 dark:bg-purple-900/20' : ''}`}
                    >
                      {/* <td className="px-6 py-4">
                        <input
                          type="checkbox"
                          checked={selectedTickets.includes(ticket.id)}
                          onChange={() => handleTicketSelect(ticket.id)}
                          className="rounded border-gray-300 dark:border-gray-600"
                        />
                      </td> */}
                      {isUnassignedQueue && canDuplicateLink && duplicateSelectMode && (
                        <td className="px-3 py-4 whitespace-nowrap">
                          <input
                            type="radio"
                            name="unassigned-duplicate-select"
                            checked={Number(selectedDuplicateTicketId) === Number(rowTicketId)}
                            onChange={() => setSelectedDuplicateTicketId(Number(rowTicketId))}
                            disabled={DUPLICATE_LINK_BLOCKED_STATUS_CODES.has(
                              normalizeTicketStatusCode(ticket),
                            )}
                            aria-label={`Select ${ticket.ticketNumber || rowTicketId} for duplicate linking`}
                            className="h-4 w-4 border-gray-300 text-amber-600 focus:ring-amber-500 disabled:opacity-40 disabled:cursor-not-allowed dark:border-gray-600 dark:bg-gray-800"
                          />
                        </td>
                      )}
                      <td className={`px-6 py-4 text-sm text-gray-700 dark:text-gray-300 ${isUnassignedQueue ? 'font-semibold text-gray-900 dark:text-gray-100' : ''}`}>
                        {(currentPage - 1) * itemsPerPage + index + 1}
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center">
                          <div className="flex-shrink-0">
                            <div className={`p-2 bg-gray-100 dark:bg-gray-700 ${isUnassignedQueue ? 'rounded-full' : 'rounded-lg'}`}>
                              <Ticket className="h-4 w-4 text-gray-600 dark:text-gray-400" />
                            </div>
                          </div>
                          <div className="ml-4">
                            <div className={`text-sm text-gray-900 dark:text-gray-100 flex items-center gap-2 flex-wrap ${isUnassignedQueue ? 'font-semibold' : 'font-medium'}`}>
                              <span>{ticket.ticketNumber}</span>
                              {/* V1 parent-child badges — feature-flagged. Children-count
                                  badge appears when this ticket has linked children;
                                  parent badge appears when this ticket is itself a child.
                                  V1 single-level rule guarantees the two are mutually
                                  exclusive. */}
                              {ticketRelationsEnabled && (() => {
                                const parentBadge = relationBadges.ticketsAsParent?.[String(ticket.id)];
                                const childBadge = relationBadges.ticketsAsChild?.[String(ticket.id)];
                                if (parentBadge?.childrenCount > 0) {
                                  return (
                                    <span
                                      className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium bg-indigo-100 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-300"
                                      title={`This ticket has ${parentBadge.childrenCount} linked child ${parentBadge.childrenCount === 1 ? 'ticket' : 'tickets'}`}
                                    >
                                      <GitBranch className="h-3 w-3 mr-1" />
                                      {parentBadge.childrenCount} child{parentBadge.childrenCount === 1 ? '' : 'ren'}
                                    </span>
                                  );
                                }
                                if (childBadge?.parentTicketNumber) {
                                  return (
                                    <span
                                      className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300"
                                      title={`Linked under parent ${childBadge.parentTicketNumber}`}
                                    >
                                      <LinkIcon className="h-3 w-3 mr-1" />
                                      Child of {childBadge.parentTicketNumber}
                                    </span>
                                  );
                                }
                                return null;
                              })()}
                            </div>
                            <div className="text-sm text-gray-500 dark:text-gray-400 truncate max-w-xs">
                              {ticket.title}
                            </div>
                            {!isUnassignedQueue && (
                            <div className="flex items-center space-x-2 mt-1">
                              <span className="text-xs text-gray-500 dark:text-gray-400 capitalize">
                                {ticket.inquiryCategoryName || ticket.category}
                              </span>
                              {ticket.attachments && ticket.attachments.length > 0 && (
                                <div className="flex items-center space-x-1">
                                  <Paperclip className="h-3 w-3 text-gray-400" />
                                  <span className="text-xs text-gray-500 dark:text-gray-400">
                                    {ticket.attachments.length}
                                  </span>
                                </div>
                              )}
                            </div>
                            )}
                            {/* {(ticket.countryName || ticket.countyName || ticket.subCountyName || ticket.wardName) && (
                              <div className="text-xs text-gray-500 dark:text-gray-400 mt-1 truncate max-w-xs">
                                📍 {[
                                  ticket.countryName,
                                  ticket.countyName,
                                  ticket.subCountyName,
                                  ticket.wardName
                                ].filter(Boolean).join(' > ')}
                              </div>
                            )} */}
                          </div>
                        </div>
                      </td>
                      {!isUnassignedQueue && (
                        <td className="px-6 py-4">
                          <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${getPriorityColor(ticket.inquiryPriorityLevelName || ticket.priority || 'medium')}`}>
                            {(ticket.inquiryPriorityLevelName || ticket.priorityName || ticket.priority || '—').toUpperCase()}
                          </span>
                        </td>
                      )}
                      <td className="px-6 py-4">
                        <div className="flex items-center space-x-2">
                          <span className={`inline-flex px-2.5 py-1 text-xs font-medium rounded-full ${isUnassignedQueue ? getUnassignedStatusBadgeClass(ticket.status) : getStatusColor(ticket.status)}`}>
                            {ticket.status.toUpperCase()}
                          </span>
                          {isReopened && (
                            <span className="inline-flex px-2 py-1 text-xs font-medium rounded-full text-purple-700 dark:text-purple-300 bg-purple-200 dark:bg-purple-800 border border-purple-300 dark:border-purple-600">
                              REOPENED
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        {ticket.assignedAgent ? (
                          <div className="flex items-center">
                            <User className="h-4 w-4 text-gray-400 mr-2" />
                            <span className="text-sm text-gray-900 dark:text-gray-100">
                              {ticket.assignedAgent}
                            </span>
                          </div>
                        ) : (
                          <span className="text-sm text-gray-500 dark:text-gray-400">Unassigned</span>
                        )}
                      </td>
                      {title !== "Unassigned Tickets" && (
                        <td className="px-6 py-4">
                          <div className="flex items-center">
                            <SLAIcon className={`h-4 w-4 mr-2 ${getSLAStatusColor(ticket.slaStatus)}`} />
                            <span className={`text-sm ${getSLAStatusColor(ticket.slaStatus)}`}>
                              {ticket.slaTimeRemaining !== null && ticket.slaTimeRemaining !== undefined
                                ? formatTimeRemaining(ticket.slaTimeRemaining)
                                : 'N/A'}
                            </span>
                          </div>
                        </td>
                      )}
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-900 dark:text-gray-100">
                          {isUnassignedQueue
                            ? formatUnassignedCreatedDate(ticket.createdAt)
                            : new Date(ticket.createdAt).toLocaleDateString()}
                        </div>
                        <div className="text-sm text-gray-500 dark:text-gray-400">
                          {isUnassignedQueue
                            ? formatUnassignedCreatedTime(ticket.createdAt)
                            : new Date(ticket.createdAt).toLocaleTimeString()}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center space-x-2">
                          {hasPermission?.canUpdate && (
                            <button
                              onClick={() => handleViewTicket(ticket.id, { viewOnly: false })}
                              className="px-3 py-1.5 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
                              title="Edit ticket"
                            >
                              <div className="flex items-center space-x-1">
                                <Pen className="h-4 w-4" />
                                <span>Edit</span>
                              </div>
                            </button>
                          )}
                          {/* View Ticket Button */}
                          {hasPermission?.canView && (
                          <button
                            onClick={() => handleViewTicket(ticket.id, { viewOnly: true })}
                            className="px-3 py-1.5 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
                            title="View Ticket"
                          >
                            <div className="flex items-center space-x-1">
                              <Eye className="h-4 w-4" />
                              <span>View</span>
                            </div>
                          </button>
                          )}
                          {/* Assign Button */}
                          <button
                            onClick={() => openAssignModal(ticket)}
                            className="px-3 py-1.5 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md transition-colors flex items-center space-x-1"
                            title="Assign Ticket"
                          >
                            <UserPlus className="h-4 w-4" />
                            <span>{isUnassignedQueue ? '+ Assign' : 'Assign'}</span>
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* old Pagination */}
          {displayTotalPages > 1 && (
            <div className="bg-white dark:bg-gray-800 px-4 py-3 border-t border-gray-200 dark:border-gray-700 sm:px-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <p className="text-sm text-gray-700 dark:text-gray-300">
                    Showing <span className="font-medium">{startIndex}</span> to{' '}
                    <span className="font-medium">{endIndex}</span> of{' '}
                    <span className="font-medium">{displayTotalElements}</span> results
                  </p>
                </div>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                    disabled={currentPage === 1}
                    className="px-3 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </button>
                  {/* {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                    <button
                      key={page}
                      onClick={() => setCurrentPage(page)}
                      className={`px-3 py-1 text-sm border rounded-md ${
                        page === currentPage
                          ? 'bg-blue-600 text-white border-blue-600'
                          : 'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300'
                      }`}
                    >
                      {page}
                    </button>
                  ))} */}
                  {/* First Page */}
                  <button
                    onClick={() => setCurrentPage(1)}
                    className={`px-3 py-1 text-sm border rounded-md ${currentPage === 1
                        ? 'bg-blue-600 text-white border-blue-600'
                        : 'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300'
                      }`}
                  >
                    1
                  </button>

                  {/* Ellipsis before */}
                  {currentPage > 4 && <span className="px-2">...</span>}

                  {/* Middle Pages */}
                  {getPageNumbers().map(page => (
                    <button
                      key={page}
                      onClick={() => setCurrentPage(page)}
                      className={`px-3 py-1 text-sm border rounded-md ${page === currentPage
                          ? 'bg-blue-600 text-white border-blue-600'
                          : 'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300'
                        }`}
                    >
                      {page}
                    </button>
                  ))}

                  {/* Ellipsis after */}
                  {currentPage < displayTotalPages - 3 && <span className="px-2">...</span>}

                  {/* Last Page */}
                  <button
                    onClick={() => setCurrentPage(displayTotalPages)}
                    className={`px-3 py-1 text-sm border rounded-md ${currentPage === displayTotalPages
                        ? 'bg-blue-600 text-white border-blue-600'
                        : 'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300'
                      }`}
                  >
                    {displayTotalPages}
                  </button>


                  <button
                    onClick={() => setCurrentPage(prev => Math.min(prev + 1, displayTotalPages))}
                    disabled={currentPage === displayTotalPages}
                    className="px-3 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ChevronRight className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Assign Ticket Modal */}
      <Modal
        isOpen={assignModalOpen}
        onClose={closeAssignModal}
        title="Assign Ticket"
        size="md"
      >
        <div className="space-y-5">
          {/* <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {assignScopeSelfOnly ? 'Assignment' : 'How do you want to assign?'}
            </label>
            {assignScopeSelfOnly ? (
              <p className="text-sm text-gray-600 dark:text-gray-400 rounded-lg bg-gray-50 dark:bg-gray-800/80 border border-gray-200 dark:border-gray-700 px-3 py-2.5">
                As an agent, you can only assign this ticket to yourself. Confirm with <span className="font-medium">Assign ticket</span> below.
              </p>
            ) : (
              <select
                value={assignMode}
                onChange={(e) => {
                  const m = e.target.value;
                  setAssignMode(m);
                  if (m === 'me') {
                    setSelectedTeam('');
                    setSelectedAgent('');
                  } else if (m === 'team') {
                    setSelectedAgent('');
                  } else if (m === 'agent') {
                    setSelectedTeam('');
                  }
                }}
                className="w-full px-3 py-2.5 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="me">Assign to myself</option>
                <option value="team">Assign to a team</option>
                <option value="agent">Assign to an Instructor</option>
              </select>
            )}
          </div> */}

          {/* {assignMode === 'me' && !assignScopeSelfOnly && (
            <p className="text-sm text-gray-600 dark:text-gray-400 rounded-lg bg-gray-50 dark:bg-gray-800/80 border border-gray-200 dark:border-gray-700 px-3 py-2">
              The ticket will be assigned to your account. Click <span className="font-medium">Assign ticket</span> below to confirm.
            </p>
          )} */}

          {assignMode === 'team' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Team
              </label>
              <select
                value={selectedTeam}
                onChange={(e) => setSelectedTeam(e.target.value)}
                disabled={loadingTeams}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
              >
                <option value="">Select a team...</option>
                {teams.map((team) => (
                  <option key={team.teamId || team.id} value={team.teamId || team.id}>
                    {team.teamName || team.name || `Team ${team.teamId || team.id}`}
                  </option>
                ))}
              </select>
              {loadingTeams && (
                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">Loading teams...</p>
              )}
            </div>
          )}

          {assignMode !== 'team' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Priority <span className="text-red-500 font-semibold">*</span>
              </label>
              <select
                value={selectedPriorityId}
                onChange={(e) => handleAssignPriorityChange(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Select a priority...</option>
                {assignmentPriorities.map((priority) => {
                  const pid = priority.id ?? priority.inquiryPriorityLevelId;
                  return (
                    <option key={pid} value={pid}>
                      {priority.priorityLevelName || priority.name || priority.priorityLevelCode || 'Unknown'}
                    </option>
                  );
                })}
              </select>
            </div>
          )}

          {assignMode === 'agent' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {assignResponsibleRoleName
                  || (selectedPriorityId ? 'Responsible role' : 'Select a priority first...')}
              </label>
              <select
                value={selectedAgent}
                onChange={(e) => setSelectedAgent(e.target.value)}
                disabled={loadingAgents || !selectedPriorityId}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
              >
                <option value="">
                  {selectedPriorityId
                    ? (assignResponsibleRoleName
                      ? `Select ${assignResponsibleRoleName}...`
                      : 'Select a user...')
                    : 'Select a priority first...'}
                </option>
                {agents.map((agent) => (
                  <option key={agent.userId || agent.id} value={agent.userId || agent.id}>
                    {agent.name || agent.email || `User ${agent.userId || agent.id}`}
                  </option>
                ))}
              </select>
              {loadingAgents && (
                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                  {assignResponsibleRoleName
                    ? `Loading ${assignResponsibleRoleName}...`
                    : 'Loading users...'}
                </p>
              )}
            </div>
          )}

          <div className="pt-1 border-t border-gray-200 dark:border-gray-700">
            <button
              type="button"
              onClick={handleUnifiedAssign}
              disabled={
                assignSubmitting ||
                (assignMode === 'team' && (!selectedTeam || loadingTeams)) ||
                (assignMode === 'agent' && (!selectedAgent || loadingAgents || !selectedPriorityId)) ||
                (assignMode !== 'team' && !selectedPriorityId)
              }
              className="w-full px-4 py-3 rounded-lg bg-blue-600 hover:bg-blue-700 text-white font-medium disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
            >
              <CheckCircle className="h-5 w-5 shrink-0" />
              {assignSubmitting ? 'Assigning…' : 'Assign ticket'}
            </button>
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={assignConflictOpen}
        onClose={closeAssignConflictModal}
        title="Ticket already assigned"
        size="sm"
        footer={
          <button
            type="button"
            onClick={closeAssignConflictModal}
            className="w-full rounded-lg bg-blue-600 px-4 py-2.5 text-sm font-medium text-white transition-colors hover:bg-blue-700"
          >
            OK
          </button>
        }
      >
        <div className="flex gap-3">
          <AlertCircle className="h-8 w-8 shrink-0 text-amber-500" aria-hidden />
          <p className="min-w-0 text-sm leading-relaxed text-gray-700 dark:text-gray-300">
            {assignConflictMessage}
          </p>
        </div>
      </Modal>

      {/* View Ticket Information Modal - Only for Unassigned Tickets */}
      {title === "Unassigned Tickets" && (
        <Modal
          isOpen={viewTicketModalOpen}
          onClose={closeViewTicketModal}
          title="Ticket Information"
          size="lg"
        >
          {selectedTicketForView && (
            <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
              {/* Priority and Status Badges */}
              <div className="flex items-center justify-end space-x-2 mb-0">
                {/* Priority Badge — Inquiry Priority Level when present */}
                <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${getPriorityColor(selectedTicketForView.inquiryPriorityLevelName || selectedTicketForView.priority || 'medium')}`}>
                  {(selectedTicketForView.inquiryPriorityLevelName || selectedTicketForView.priorityName || selectedTicketForView.priority || '—').toUpperCase()}
                </span>
                {/* Status badge — read-only in Unassigned ticket modal */}
                <span className={`inline-flex px-2.5 py-1 text-xs font-medium rounded-full ${getUnassignedStatusBadgeClass(getTicketStatusCode(selectedTicketForView) || selectedTicketForView.status)}`}>
                  {(selectedTicketForView.status || getTicketStatusCode(selectedTicketForView) || 'open').toUpperCase()}
                </span>
              </div>

              {/* Two Column Layout */}
              <div className="grid min-w-0 grid-cols-2 gap-6 mb-6">
                {/* Left Column */}
                <div className="min-w-0 space-y-4">
                  <div>
                    <label className="text-sm font-medium text-gray-500 dark:text-gray-400">Subject</label>
                    <p className="mt-1 max-w-full break-words text-sm text-gray-900 [overflow-wrap:anywhere] dark:text-gray-100">
                      {(selectedTicketForView.subject || selectedTicketForView.title || '').trim() || '—'}
                    </p>
                  </div>
                  {title === 'Unassigned Tickets' && !viewTicketModalViewOnly && (
                    <QueueConfidentialityLevelControl
                      ticketId={selectedTicketForView.id ?? selectedTicketForView.ticketId}
                      initialLevel={selectedTicketForView.confidentialityLevel}
                      disabled={savingInquiryClassification}
                      onSaved={({ removedFromQueue }) => {
                        if (removedFromQueue) {
                          setViewTicketModalOpen(false);
                          setSelectedTicketForView(null);
                        }
                        fetchQueueDataRef.current?.();
                      }}
                    />
                  )}
                  <div className="text-sm font-medium text-gray-500 dark:text-gray-400">Inquiry classification</div>
                  {canClassifyInquiry && !viewTicketModalViewOnly ? (
                    <div className="space-y-3 rounded-md border border-gray-200 dark:border-gray-600 p-3">
                      <div>
                        <label className="text-xs text-gray-500 dark:text-gray-400 mb-1 block">Inquiry Type</label>
                        <select
                          className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                          value={draftInquiryTypeId}
                          onChange={(e) => handleDraftInquiryTypeChange(e.target.value)}
                          disabled={savingInquiryClassification}
                        >
                          <option value="">Select</option>
                          {inquiryTypes.map((type) => (
                            <option key={type.id} value={String(type.id)}>
                              {type.inquiryTypeName || type.name || `Type ${type.id}`}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="text-xs text-gray-500 dark:text-gray-400 mb-1 block">Inquiry Category</label>
                        <select
                          className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 disabled:opacity-70"
                          value={draftInquiryCategoryId}
                          onChange={(e) => handleDraftInquiryCategoryChange(e.target.value)}
                          disabled={!draftInquiryTypeId || savingInquiryClassification}
                        >
                          <option value="">Select</option>
                          {inquiryCategories.map((cat) => (
                            <option key={cat.id} value={String(cat.id)}>
                              {cat.inquiryCategoryName || cat.name || `Category ${cat.id}`}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="text-xs text-gray-500 dark:text-gray-400 mb-1 block">Inquiry Sub Category</label>
                        <select
                          className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 disabled:opacity-70"
                          value={draftInquirySubCategoryId}
                          onChange={(e) => setDraftInquirySubCategoryId(e.target.value)}
                          disabled={!draftInquiryCategoryId || savingInquiryClassification}
                        >
                          <option value="">Select</option>
                          {inquirySubCategories.map((sub) => (
                            <option key={sub.id} value={String(sub.id)}>
                              {sub.inquirySubCategoryName || sub.name || `Sub Category ${sub.id}`}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="text-xs text-gray-500 dark:text-gray-400 mb-1 block">Inquiry Priority</label>
                        <p className="px-3 py-2 text-sm rounded-md border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-800/50 text-gray-900 dark:text-gray-100">
                          {selectedTicketForView.inquiryPriorityLevelName
                            || selectedTicketForView.priorityName
                            || (selectedTicketForView.inquiryPriorityLevelId != null
                              ? `Priority ${selectedTicketForView.inquiryPriorityLevelId}`
                              : 'Not Prioritised')}
                        </p>
                      </div>
                      <div className="flex justify-end gap-2 pt-1">
                        <button
                          type="button"
                          onClick={cancelInquiryClassificationEdit}
                          disabled={savingInquiryClassification}
                          className="px-3 py-1.5 text-xs font-medium text-gray-700 dark:text-gray-200 border border-gray-300 dark:border-gray-600 rounded-md disabled:opacity-50"
                        >
                          Cancel
                        </button>
                        <button
                          type="button"
                          onClick={saveInquiryClassification}
                          disabled={savingInquiryClassification}
                          className="px-3 py-1.5 text-xs font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md disabled:opacity-50"
                        >
                          {savingInquiryClassification ? 'Saving…' : 'Save'}
                        </button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <div>
                        <label className="text-sm font-medium text-gray-500 dark:text-gray-400">Inquiry Type</label>
                        <p className="mt-1 max-w-full break-words text-sm text-gray-900 [overflow-wrap:anywhere] dark:text-gray-100">
                          {selectedTicketForView.inquiryTypeName || (selectedTicketForView.inquiryTypeId != null ? `Type ${selectedTicketForView.inquiryTypeId}` : 'Not Set')}
                        </p>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-500 dark:text-gray-400">Inquiry Category</label>
                        <p className="mt-1 max-w-full break-words text-sm text-gray-900 [overflow-wrap:anywhere] dark:text-gray-100">
                          {selectedTicketForView.inquiryCategoryName || (selectedTicketForView.inquiryCategoryId != null ? `Category ${selectedTicketForView.inquiryCategoryId}` : 'Not Set')}
                        </p>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-500 dark:text-gray-400">Inquiry Sub Category</label>
                        <p className="mt-1 max-w-full break-words text-sm text-gray-900 [overflow-wrap:anywhere] dark:text-gray-100">
                          {selectedTicketForView.inquirySubCategoryName || (selectedTicketForView.inquirySubCategoryId != null ? `Sub Category ${selectedTicketForView.inquirySubCategoryId}` : 'Not Set')}
                        </p>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-500 dark:text-gray-400">Inquiry Priority</label>
                        <p className="mt-1 max-w-full break-words text-sm text-gray-900 [overflow-wrap:anywhere] dark:text-gray-100">
                          {selectedTicketForView.inquiryPriorityLevelName
                            || selectedTicketForView.priorityName
                            || (selectedTicketForView.inquiryPriorityLevelId != null ? `Priority ${selectedTicketForView.inquiryPriorityLevelId}` : 'Not Set')}
                        </p>
                      </div>
                      {(selectedTicketForView.category || selectedTicketForView.categoryName) && (
                        <div>
                          <label className="text-sm font-medium text-gray-500 dark:text-gray-400">Category</label>
                          <p className="mt-1 text-sm text-gray-900 dark:text-gray-100">
                            {selectedTicketForView.category || selectedTicketForView.categoryName}
                          </p>
                        </div>
                      )}
                    </>
                  )}
                  <div>
                    <label className="text-sm font-medium text-gray-500 dark:text-gray-400">Created</label>
                    <p className="text-sm text-gray-900 dark:text-gray-100 mt-1">
                      {selectedTicketForView.createdAt
                        ? new Date(selectedTicketForView.createdAt).toLocaleString('en-GB', {
                          day: '2-digit',
                          month: '2-digit',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit',
                          second: '2-digit'
                        })
                        : 'N/A'}
                    </p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500 dark:text-gray-400">Last Updated</label>
                    <p className="text-sm text-gray-900 dark:text-gray-100 mt-1">
                      {selectedTicketForView.updatedAt
                        ? new Date(selectedTicketForView.updatedAt).toLocaleString('en-GB', {
                          day: '2-digit',
                          month: '2-digit',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit',
                          second: '2-digit'
                        })
                        : 'N/A'}
                    </p>
                  </div>
                  <div className="flex items-center space-x-2">
                    <User className="h-4 w-4 text-gray-400" />
                    <span className="text-sm text-gray-600 dark:text-gray-400">Requester:</span>
                    <span className="min-w-0 max-w-full break-words text-sm font-medium text-gray-900 [overflow-wrap:anywhere] dark:text-gray-100">
                      {loadingRequester ? (
                        <span className="text-gray-400">Loading...</span>
                      ) : (
                        requesterUser?.name
                        || formatRequesterDisplay(selectedTicketForView)
                        || (selectedTicketForView.requesterId ? `User ${selectedTicketForView.requesterId}` : 'Unknown')
                      )}
                    </span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <User className="h-4 w-4 text-gray-400" />
                    <span className="text-sm text-gray-600 dark:text-gray-400">Assigned:</span>
                    <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                      {loadingAssigned ? (
                        <span className="text-gray-400">Loading...</span>
                      ) : (
                        assignedUser?.name || selectedTicketForView.assignedAgent || 'Unassigned'
                      )}
                    </span>
                  </div>
                </div>

                {/* Right Column - Location Section */}
                <div className="min-w-0 space-y-4">
                  <div>
                    <label className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-3 block">Location</label>
                    <div className="grid grid-cols-2 gap-3">
                      {/* Country Dropdown */}
                      <div>
                        <label className="text-xs text-gray-500 dark:text-gray-400 mb-1 block">Country *</label>
                        <select
                          className={`w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:cursor-not-allowed disabled:opacity-70 ${viewTicketModalViewOnly || updatingLocation || loadingLocations.countries ? 'cursor-not-allowed' : 'cursor-pointer'}`}
                          value={selectedCountryId || ''}
                          onChange={handleCountryChange}
                          disabled={viewTicketModalViewOnly || updatingLocation || loadingLocations.countries}
                        >
                          <option value="">Select Country</option>
                          {countries.map((country) => (
                            <option
                              key={country.id || country.countryId}
                              value={country.id || country.countryId}
                            >
                              {country.name || country.countryName || `Country ${country.id || country.countryId}`}
                            </option>
                          ))}
                        </select>
                      </div>

                      {/* County Dropdown */}
                      <div>
                        <label className="text-xs text-gray-500 dark:text-gray-400 mb-1 block">County</label>
                        {selectedCountryId ? (
                          <select
                            className={`w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:cursor-not-allowed disabled:opacity-70 ${viewTicketModalViewOnly || updatingLocation || loadingLocations.counties ? 'cursor-not-allowed' : 'cursor-pointer'}`}
                            value={selectedCountyId || ''}
                            onChange={handleCountyChange}
                            disabled={viewTicketModalViewOnly || updatingLocation || loadingLocations.counties}
                          >
                            <option value="">Select</option>
                            {counties.map((county) => (
                              <option
                                key={county.id || county.countyId}
                                value={county.id || county.countyId}
                              >
                                {county.name || county.countyName || `County ${county.id || county.countyId}`}
                              </option>
                            ))}
                          </select>
                        ) : (
                          <select
                            className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-gray-100 dark:bg-gray-800 text-gray-400 dark:text-gray-500 cursor-not-allowed"
                            disabled
                          >
                            <option value="">Select</option>
                          </select>
                        )}
                      </div>

                      {/* Sub County Dropdown */}
                      <div>
                        <label className="text-xs text-gray-500 dark:text-gray-400 mb-1 block">Sub County</label>
                        {selectedCountyId ? (
                          <select
                            className={`w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:cursor-not-allowed disabled:opacity-70 ${viewTicketModalViewOnly || updatingLocation || loadingLocations.subCounties ? 'cursor-not-allowed' : 'cursor-pointer'}`}
                            value={selectedSubCountyId || ''}
                            onChange={handleSubCountyChange}
                            disabled={viewTicketModalViewOnly || updatingLocation || loadingLocations.subCounties}
                          >
                            <option value="">Select</option>
                            {subCounties.map((subCounty) => (
                              <option
                                key={subCounty.id || subCounty.subCountyId}
                                value={subCounty.id || subCounty.subCountyId}
                              >
                                {subCounty.name || subCounty.subCountyName || `Sub County ${subCounty.id || subCounty.subCountyId}`}
                              </option>
                            ))}
                          </select>
                        ) : (
                          <select
                            className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-gray-100 dark:bg-gray-800 text-gray-400 dark:text-gray-500 cursor-not-allowed"
                            disabled
                          >
                            <option value="">Select</option>
                          </select>
                        )}
                      </div>

                      {/* Ward Dropdown */}
                      {/* <div>
                      <label className="text-xs text-gray-500 dark:text-gray-400 mb-1 block">Ward</label>
                      {selectedSubCountyId ? (
                        <select
                          className={`w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 ${updatingLocation || loadingLocations.wards ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                          value={selectedWardId || ''}
                          onChange={handleWardChange}
                          disabled={updatingLocation || loadingLocations.wards}
                        >
                          <option value="">Select</option>
                          {wards.map((ward) => (
                            <option 
                              key={ward.id || ward.wardId} 
                              value={ward.id || ward.wardId}
                            >
                              {ward.name || ward.wardName || `Ward ${ward.id || ward.wardId}`}
                            </option>
                          ))}
                        </select>
                      ) : (
                        <select
                          className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-gray-100 dark:bg-gray-800 text-gray-400 dark:text-gray-500 cursor-not-allowed"
                          disabled
                        >
                          <option value="">Select</option>
                        </select>
                      )}
                    </div> */}

                      {/* Update Location Button - Span full width */}
                      {(selectedCountryId || selectedCountyId || selectedSubCountyId || selectedWardId || viewTicketModalViewOnly) && (
                        <div className="col-span-2">
                          <button
                            type="button"
                            onClick={handleLocationUpdate}
                            disabled={viewTicketModalViewOnly || updatingLocation}
                            className="w-full px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
                          >
                            {updatingLocation ? (
                              <>
                                <RefreshCw className="h-4 w-4 animate-spin" />
                                <span>Updating...</span>
                              </>
                            ) : (
                              <>
                                <Check className="h-4 w-4" />
                                <span>Update Location</span>
                              </>
                            )}
                          </button>
                        </div>
                      )}

                      {/* Display current location if exists but no selection made */}
                      {!selectedCountryId && !selectedCountyId && !selectedSubCountyId && !selectedWardId &&
                        (selectedTicketForView.countryName || selectedTicketForView.countyName || selectedTicketForView.subCountyName || selectedTicketForView.wardName) && (
                          <div className="col-span-2 text-sm text-gray-600 dark:text-gray-400">
                            Current: {[
                              selectedTicketForView.countryName,
                              selectedTicketForView.countyName,
                              selectedTicketForView.subCountyName,
                              selectedTicketForView.wardName
                            ].filter(Boolean).join(' > ') || 'No location set'}
                          </div>
                        )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Description Section - Full Width */}
              <div className="mb-6 min-w-0">
                <label className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2 block">Description</label>
                <textarea
                  className={`min-w-0 max-w-full break-words whitespace-pre-wrap [overflow-wrap:anywhere] w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 resize-none disabled:cursor-not-allowed disabled:opacity-70 ${viewTicketModalViewOnly ? '' : 'focus:outline-none focus:ring-2 focus:ring-blue-500'}`}
                  rows={6}
                  value={selectedTicketForView.description || selectedTicketForView.title || ''}
                  readOnly
                  disabled={viewTicketModalViewOnly}
                />
              </div>

              {/* Attachments */}
              <div className="mb-6 min-w-0 border-t border-gray-200 dark:border-gray-700 pt-4">
                <label className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-3 flex items-center">
                  <Paperclip className="h-4 w-4 mr-2" aria-hidden />
                  Attachments
                </label>
                {loadingViewModalAttachments ? (
                  <div className="flex items-center justify-center py-6 text-sm text-gray-500 dark:text-gray-400">
                    <RefreshCw className="h-4 w-4 mr-2 animate-spin" aria-hidden />
                    Loading attachments…
                  </div>
                ) : viewModalAttachments.length > 0 ? (
                  <div className="space-y-2">
                    {viewModalAttachments.map((attachment) => (
                      <div
                        key={attachment.id || attachment.attachmentId}
                        className="flex items-start justify-between gap-3 p-3 rounded-lg border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-700/50 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                      >
                        <div className="flex gap-2 flex-1 min-w-0">
                          <Paperclip className="h-4 w-4 text-gray-400 flex-shrink-0 mt-0.5" aria-hidden />
                          <div className="min-w-0 flex-1 space-y-0.5">
                            <p
                              className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate"
                              title={attachment.name || attachment.fileName}
                            >
                              {attachment.name || attachment.fileName || 'Unnamed file'}
                            </p>
                            {(attachment.uploaderName || attachment.uploaderRole) && (
                              <p className="text-xs text-gray-600 dark:text-gray-300 break-words">
                                Uploaded by:{' '}
                                <span className="font-medium text-gray-800 dark:text-gray-100">
                                  {attachment.uploaderName || 'Unknown'}
                                  {attachment.uploaderRole ? ` (${attachment.uploaderRole})` : ''}
                                </span>
                              </p>
                            )}
                            <p className="text-xs text-gray-500 dark:text-gray-400">
                              {formatTicketFileUploadedAt(attachment.createdOn || attachment.createdAt)}
                            </p>
                            {attachment.size ? (
                              <p className="text-xs text-gray-500 dark:text-gray-400">
                                ({(attachment.size / 1024).toFixed(2)} KB)
                              </p>
                            ) : null}
                          </div>
                        </div>
                        <div className="flex flex-shrink-0 items-center gap-1 pt-0.5">
                          <button
                            type="button"
                            onClick={() => handleViewModalAttachment(attachment)}
                            className="p-2 text-gray-500 hover:text-emerald-600 dark:text-gray-400 dark:hover:text-emerald-400 rounded-md hover:bg-emerald-50 dark:hover:bg-emerald-900/20 transition-colors"
                            title={`View ${attachment.name || attachment.fileName || 'attachment'}`}
                            aria-label={`View ${attachment.name || attachment.fileName || 'attachment'}`}
                          >
                            <Eye className="h-4 w-4" />
                          </button>
                          <button
                            type="button"
                            onClick={() => handleDownloadViewModalAttachment(attachment)}
                            className="p-2 text-gray-500 hover:text-blue-600 dark:text-gray-400 dark:hover:text-blue-400 rounded-md hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors"
                            title={`Download ${attachment.name || attachment.fileName || 'attachment'}`}
                            aria-label={`Download ${attachment.name || attachment.fileName || 'attachment'}`}
                          >
                            <Download className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-6 rounded-lg border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-700/30">
                    <Paperclip className="h-8 w-8 text-gray-300 dark:text-gray-500 mx-auto mb-2" aria-hidden />
                    <p className="text-sm text-gray-600 dark:text-gray-400">No attachments</p>
                  </div>
                )}
              </div>

              {ticketRelationsEnabled && (
                <div className="mb-6 min-w-0">
                  <LinkedDuplicatesPanel
                    bundle={viewModalRelationsBundle}
                    isLoading={viewModalRelationsLoading}
                    canLink={canShowViewModalDuplicateLinkAction}
                    onOpenLinkDuplicate={() => setShowAttachDuplicateModal(true)}
                    canCloseAsDuplicate={canCloseViewModalAsDuplicate}
                    onCloseAsDuplicate={handleOpenViewModalCloseAsDuplicate}
                  />
                </div>
              )}

              {selectedTicketForView.customFieldValues &&
                typeof selectedTicketForView.customFieldValues === 'object' &&
                Object.keys(selectedTicketForView.customFieldValues).length > 0 && (
                  <div className="mb-6 min-w-0 border-t border-gray-200 dark:border-gray-700 pt-4">
                    <label className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2 block">
                      Custom fields
                    </label>
                    <dl className="space-y-2">
                      {Object.entries(selectedTicketForView.customFieldValues).map(([fieldCode, fieldValue]) => (
                        <div key={fieldCode} className="flex min-w-0 flex-wrap items-start gap-2">
                          <dt className="shrink-0 text-sm font-medium text-gray-700 dark:text-gray-300">
                            {fieldCode.replace(/_/g, ' ')}:
                          </dt>
                          <dd className="min-w-0 flex-1 text-sm text-gray-900 [overflow-wrap:anywhere] whitespace-pre-wrap break-words dark:text-gray-100">
                            {formatCustomFieldDisplayValue(fieldValue)}
                          </dd>
                        </div>
                      ))}
                    </dl>
                  </div>
                )}

              {/* Action Buttons - Bottom Right */}
              <div className="flex justify-end items-center space-x-3 pt-4 border-t border-gray-200 dark:border-gray-700">
                <button
                  type="button"
                  onClick={() => {
                    // Handle update - could update description, category, status, location
                    toast.success('Ticket updated successfully');
                    closeViewTicketModal();
                  }}
                  disabled={viewTicketModalViewOnly}
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md transition-colors flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Check className="h-4 w-4" />
                  <span>Update</span>
                </button>
              </div>
            </div>
          )}
        </Modal>
      )}

      {title === 'Unassigned Tickets' && canDuplicateLink && selectedTicketForView && (
        <AttachDuplicateModal
          currentTicketId={selectedTicketForView.id}
          currentTicketNumber={selectedTicketForView.ticketNumber}
          linkMode={viewModalDuplicateLinkMode}
          isOpen={showAttachDuplicateModal}
          onClose={() => setShowAttachDuplicateModal(false)}
          onLinked={handleViewModalDuplicateLinked}
        />
      )}

      {isUnassignedQueue && canDuplicateLink && selectedDuplicateTicket && (
        <AttachDuplicateModal
          currentTicketId={selectedDuplicateTicket.ticketId || selectedDuplicateTicket.id}
          currentTicketNumber={selectedDuplicateTicket.ticketNumber}
          linkMode="pick-canonical"
          isOpen={showQueueDuplicateModal}
          onClose={exitDuplicateSelectMode}
          onLinked={handleQueueDuplicateLinked}
        />
      )}

      {title === 'Unassigned Tickets' && selectedTicketForView && (
        <CloseAsDuplicateModal
          isOpen={showCloseAsDuplicateModal && Boolean(viewModalRelationsBundle?.duplicateOf)}
          onClose={() => {
            if (!closingAsDuplicate) {
              setShowCloseAsDuplicateModal(false);
              setCloseAsDuplicateError('');
            }
          }}
          currentTicketNumber={selectedTicketForView.ticketNumber}
          canonicalTicketNumber={
            viewModalRelationsBundle?.duplicateOf?.ticketNumber
            || (viewModalRelationsBundle?.duplicateOf?.ticketId
              ? `#${viewModalRelationsBundle.duplicateOf.ticketId}`
              : undefined)
          }
          reason={closeAsDuplicateReason}
          onReasonChange={(value) => {
            setCloseAsDuplicateReason(value);
            if (closeAsDuplicateError) setCloseAsDuplicateError('');
          }}
          error={closeAsDuplicateError}
          submitting={closingAsDuplicate}
          onSubmit={handleViewModalCloseAsDuplicate}
        />
      )}
    </div>
  );
};

export default TicketQueue;

export const apiGet = (endpoint, params) => apiRequest(endpoint, { method: "GET", params });
